﻿import { useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  type DataInventoryType,
  type FaciliooDocumentGroupOption,
  type FaciliooDocumentGroupsResult,
  type FaciliooEnsureDocumentGroupResult,
  type FaciliooResetDocumentGroupResult,
  type FaciliooWebhookConnectionTestResult,
  type FaciliooWebhookPingResult,
  type MicrosoftMailFolderOption,
  type MicrosoftMailFoldersResult,
  type MicrosoftMailStatus,
  type FaciliooWebhookTargetRegisterResult,
  type FaciliooWebhookTargetRegenerateResult,
  type FaciliooWebhookTargetStatus
} from "../app/api";
import type { AuthUser } from "../app/auth";
import { resolveHostSlugFromHostname } from "../app/tenant-host";
import { defaultUiSettings, type UiSettings } from "../app/ui-settings";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import type { FaciliooKeyTableRow } from "../shared/ownerNaming";

const brevoProviderLogo = new URL("../assets/mail-provider-brevo.svg", import.meta.url).toString();
const microsoftProviderLogo = new URL("../assets/mail-provider-microsoft.svg", import.meta.url).toString();

const MailEnvelopeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M4.8 6.8h14.4v10.4H4.8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="m5.4 7.4 6.6 5.2 6.6-5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type SettingsPageProps = {
  settings: UiSettings;
  onChange: (next: UiSettings) => void;
  authUser: AuthUser | null;
  saveStatus?: { type: "ok" | "info"; text: string } | null;
};

type LetterheadSlot = "digital" | "analog" | "digital_followup" | "analog_followup";
type SettingsTabKey = "allgemein" | "facilioo" | "mailversand" | "design" | "uploads" | "datenbestand";
type UploadDropTarget =
  | "logo-backend"
  | "logo-owner"
  | "letterhead-digital"
  | "letterhead-analog"
  | "letterhead-digital-followup"
  | "letterhead-analog-followup"
  | null;
const FACILIOO_GROUP_NONE = "__none__";
const NDR_FOLDER_DEFAULT = "__default__";

const MailProviderIcon = ({ provider }: { provider: UiSettings["mail"]["provider"] }) => {
  if (provider === "microsoft_graph") {
    return (
      <span className="settings-v2-mail-provider-icon" aria-hidden="true">
        <img src={microsoftProviderLogo} alt="" />
      </span>
    );
  }

  if (provider === "brevo_api") {
    return (
      <span className="settings-v2-mail-provider-icon settings-v2-mail-provider-icon--brevo" aria-hidden="true">
        <img src={brevoProviderLogo} alt="" />
      </span>
    );
  }

  return (
    <span className="settings-v2-mail-provider-icon settings-v2-mail-provider-icon--smtp" aria-hidden="true">
      <MailEnvelopeIcon />
    </span>
  );
};

const UploadCardIcon = ({ variant }: { variant: "image" | "user" | "document" }) => {
  const icon =
    variant === "user" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M12 12.1a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.8 20.2c.7-3.5 3.2-5.5 7.2-5.5s6.5 2 7.2 5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : variant === "document" ? (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M7 3.8h7.3L18 7.5v12.7H7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M14 4v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 12h5M10 15.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ) : (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4.8 6.2h14.4v11.6H4.8z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="m8 15 3.1-3.1 2.1 2.1 1.4-1.4L19 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 9.4h.1" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
    );

  return (
    <span className={`settings-upload-card-icon settings-upload-card-icon--${variant}`} aria-hidden="true">
      {icon}
    </span>
  );
};

const UploadActionIcon = ({ kind }: { kind: "upload" | "delete" | "open" | "save" }) => {
  if (kind === "delete") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="M4.5 6.5h15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M9.5 6.5V4.75h5V6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 9l.7 10.25h8.6L17 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 11.25v5.75M14 11.25v5.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (kind === "open") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M14 5.6h4.4V10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m9.8 14.2 8.6-8.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M18.4 13.4v4.8H5.6V5.6h4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (kind === "save") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5.6 5.6h10.6l2.2 2.2v10.6H5.6z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M8.4 5.6v5h7.2v-5M9 18.2h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 5.2v9.6M8.2 8.8 12 5.2l3.8 3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.8 14.8v3.4h12.4v-3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const UploadStatusIcon = ({ kind }: { kind: "ok" | "warn" }) => {
  if (kind === "warn") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m12 4.8 8 14H4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M12 9.4v5.2M12 17.6h.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m8.8 12.2 2.2 2.2 4.2-4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const cleanKeyTableRows = (rows: FaciliooKeyTableRow[], options?: { includeBrief?: boolean }): FaciliooKeyTableRow[] =>
  rows.map((row) => ({
    definition: String(row.definition ?? "").trim(),
    mapping: String(row.mapping ?? "").trim(),
    ...(options?.includeBrief
      ? {
          briefanrede_1: row.briefanrede_1 ? String(row.briefanrede_1 ?? "").trim() : "",
          briefanrede_2: row.briefanrede_2 ? String(row.briefanrede_2 ?? "").trim() : ""
        }
      : {})
  }));

export const SettingsPage = ({ settings, onChange, authUser, saveStatus }: SettingsPageProps) => {
  const platformPortalAliasesRaw =
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_PLATFORM_PORTAL_ALIASES ?? "plattform,platform";
  const platformPortalAliases = new Set(
    String(platformPortalAliasesRaw)
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  );
  const host = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const hostSlug = resolveHostSlugFromHostname(host);
  const isPlatformSettingsHost = platformPortalAliases.has(hostSlug);

  const [form, setForm] = useState<UiSettings>(settings);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showBrevoApiKey, setShowBrevoApiKey] = useState(false);
  const [showFaciliooApiKey, setShowFaciliooApiKey] = useState(false);
  const [showFaciliooWebhookApiKey, setShowFaciliooWebhookApiKey] = useState(false);
  const [showFaciliooKeyTables, setShowFaciliooKeyTables] = useState(false);
  const [showFaciliooApiTokenSection, setShowFaciliooApiTokenSection] = useState(false);
  const [faciliooWebhookStatus, setFaciliooWebhookStatus] = useState<FaciliooWebhookTargetStatus | null>(null);
  const [faciliooWebhookGeneratedKey, setFaciliooWebhookGeneratedKey] = useState("");
  const [faciliooWebhookLoading, setFaciliooWebhookLoading] = useState(false);
  const [faciliooWebhookRegenerating, setFaciliooWebhookRegenerating] = useState(false);
  const [faciliooWebhookRegistering, setFaciliooWebhookRegistering] = useState(false);
  const [faciliooWebhookRegisteringUnitOnly, setFaciliooWebhookRegisteringUnitOnly] = useState(false);
  const [faciliooWebhookConnectionTesting, setFaciliooWebhookConnectionTesting] = useState(false);
  const [faciliooWebhookPingTesting, setFaciliooWebhookPingTesting] = useState(false);
  const [faciliooDocumentGroupCreating, setFaciliooDocumentGroupCreating] = useState(false);
  const [faciliooDocumentGroupResetting, setFaciliooDocumentGroupResetting] = useState(false);
  const [faciliooDocumentGroupsLoading, setFaciliooDocumentGroupsLoading] = useState(false);
  const [faciliooDocumentGroups, setFaciliooDocumentGroups] = useState<FaciliooDocumentGroupOption[]>([]);
  const [faciliooCreateGroupName, setFaciliooCreateGroupName] = useState("");
  const [faciliooCreateParentGroupId, setFaciliooCreateParentGroupId] = useState<string>(
    String(settings.management.faciliooDocumentGroupId ?? "").trim() || FACILIOO_GROUP_NONE
  );
  const [faciliooWebhookConnectionTest, setFaciliooWebhookConnectionTest] = useState<FaciliooWebhookConnectionTestResult | null>(null);
  const [faciliooWebhookPingResult, setFaciliooWebhookPingResult] = useState<FaciliooWebhookPingResult | null>(null);
  const [faciliooWebhookFeedback, setFaciliooWebhookFeedback] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [mailTestStatus, setMailTestStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [mailTestPending, setMailTestPending] = useState(false);
  const [microsoftMailStatus, setMicrosoftMailStatus] = useState<MicrosoftMailStatus | null>(null);
  const [microsoftMailPending, setMicrosoftMailPending] = useState(false);
  const [microsoftNdrFoldersLoading, setMicrosoftNdrFoldersLoading] = useState(false);
  const [microsoftNdrFolders, setMicrosoftNdrFolders] = useState<MicrosoftMailFolderOption[]>([]);
  const [microsoftNdrFoldersMailbox, setMicrosoftNdrFoldersMailbox] = useState<string>("");
  const [microsoftNdrFoldersError, setMicrosoftNdrFoldersError] = useState<string>("");
  const [mailProviderMenuOpen, setMailProviderMenuOpen] = useState(false);
  const [mailProviderMenuHovered, setMailProviderMenuHovered] = useState(false);
  const [tab, setTab] = useState<SettingsTabKey>("allgemein");
  const [dataClearType, setDataClearType] = useState<DataInventoryType>("objects");
  const [dataClearPending, setDataClearPending] = useState(false);
  const [dataClearStatus, setDataClearStatus] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [dataClearConfirm, setDataClearConfirm] = useState<{
    code: string;
    input: string[];
    error: string;
  } | null>(null);
  const [saveButtonFlash, setSaveButtonFlash] = useState(false);
  const [settingsActionFeedback, setSettingsActionFeedback] = useState<{ type: "ok" | "info"; text: string } | null>(null);
  const [letterheads, setLetterheads] = useState<{
    digital?: {
      fileName?: string;
      updatedAt?: string;
      fileUrl?: string;
      topMarginMm: number;
      bottomMarginMm: number;
      firstPageRecipientTopMm?: number;
      firstPageBodyStartMm?: number;
    };
    analog?: {
      fileName?: string;
      updatedAt?: string;
      fileUrl?: string;
      topMarginMm: number;
      bottomMarginMm: number;
      firstPageRecipientTopMm?: number;
      firstPageBodyStartMm?: number;
    };
    digital_followup?: { fileName?: string; updatedAt?: string; fileUrl?: string; topMarginMm: number; bottomMarginMm: number };
    analog_followup?: { fileName?: string; updatedAt?: string; fileUrl?: string; topMarginMm: number; bottomMarginMm: number };
  }>({});
  const [letterheadPending, setLetterheadPending] = useState<LetterheadSlot | null>(null);
  const [letterheadDeletePending, setLetterheadDeletePending] = useState<LetterheadSlot | null>(null);
  const [letterheadMarginsPending, setLetterheadMarginsPending] = useState<LetterheadSlot | null>(null);
  const [letterheadFirstPageLayoutPending, setLetterheadFirstPageLayoutPending] = useState<"digital" | "analog" | null>(null);
  const [uploadDropTarget, setUploadDropTarget] = useState<UploadDropTarget>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const ownerLogoInputRef = useRef<HTMLInputElement | null>(null);
  const letterheadDigitalInputRef = useRef<HTMLInputElement | null>(null);
  const letterheadAnalogInputRef = useRef<HTMLInputElement | null>(null);
  const letterheadDigitalFollowupInputRef = useRef<HTMLInputElement | null>(null);
  const letterheadAnalogFollowupInputRef = useRef<HTMLInputElement | null>(null);

  const faciliooDocumentGroupOptions = useMemo(() => {
    type GroupWithPath = FaciliooDocumentGroupOption & { normalizedName: string };
    const normalizePath = (value: string) =>
      value
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" / ");

    const groups: GroupWithPath[] = faciliooDocumentGroups
      .map((group) => ({
        ...group,
        normalizedName: normalizePath(String(group.groupName ?? "").trim() || `Dokumentgruppe ${group.groupId}`)
      }))
      .sort((a, b) => a.normalizedName.localeCompare(b.normalizedName, "de", { sensitivity: "base" }));

    const byParent = new Map<string, GroupWithPath[]>();
    const rootKey = "__root__";
    for (const group of groups) {
      const parts = group.normalizedName.split(" / ").map((part) => part.trim()).filter(Boolean);
      const parentPath = parts.length > 1 ? parts.slice(0, -1).join(" / ") : rootKey;
      const bucket = byParent.get(parentPath) ?? [];
      bucket.push(group);
      byParent.set(parentPath, bucket);
    }

    const ordered: Array<{ groupId: string; groupName: string; label: string }> = [];
    const visited = new Set<string>();

    const walk = (parentPath: string, level: number) => {
      const children = (byParent.get(parentPath) ?? []).sort((a, b) =>
        a.normalizedName.localeCompare(b.normalizedName, "de", { sensitivity: "base" })
      );
      for (const child of children) {
        if (visited.has(child.groupId)) continue;
        visited.add(child.groupId);
        const parts = child.normalizedName.split(" / ").map((part) => part.trim()).filter(Boolean);
        const title = parts[parts.length - 1] || child.normalizedName;
        const indent = "\u00A0\u00A0".repeat(Math.max(0, level));
        const prefix = level > 0 ? "- " : "";
        ordered.push({
          groupId: child.groupId,
          groupName: child.normalizedName,
          label: `${indent}${prefix}${title} (${child.groupId})`
        });
        walk(child.normalizedName, level + 1);
      }
    };

    walk(rootKey, 0);
    for (const group of groups) {
      if (visited.has(group.groupId)) continue;
      ordered.push({
        groupId: group.groupId,
        groupName: group.normalizedName,
        label: `${group.normalizedName} (${group.groupId})`
      });
    }
    return ordered;
  }, [faciliooDocumentGroups]);

  const facliooSelectedParentHierarchy = useMemo(() => {
    const currentId = String(faciliooCreateParentGroupId ?? "").trim();
    if (!currentId || currentId === FACILIOO_GROUP_NONE) return "";
    const selected = faciliooDocumentGroupOptions.find((group) => group.groupId === currentId);
    return String(selected?.groupName ?? "").trim();
  }, [faciliooCreateParentGroupId, faciliooDocumentGroupOptions]);

  const microsoftNdrFolderOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [{ value: NDR_FOLDER_DEFAULT, label: "keine (nur Posteingang/Inbox)" }];
    const seen = new Set<string>([NDR_FOLDER_DEFAULT.toLowerCase()]);

    for (const row of microsoftNdrFolders) {
      const value = String(row.name ?? "").trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const label = String(row.path ?? row.name ?? "").trim() || value;
      options.push({ value, label });
    }
    return options;
  }, [microsoftNdrFolders]);

  const selectedMicrosoftNdrFolderValue = useMemo(() => {
    const current = String(form.mail.microsoftNdrFolders ?? "").trim();
    if (!current) return NDR_FOLDER_DEFAULT;
    return microsoftNdrFolderOptions.some((entry) => entry.value === current) ? current : NDR_FOLDER_DEFAULT;
  }, [form.mail.microsoftNdrFolders, microsoftNdrFolderOptions]);

  const showSettingsActionFeedback = (feedback: { type: "ok" | "info"; text: string }) => {
    setSettingsActionFeedback(feedback);
    window.setTimeout(() => setSettingsActionFeedback(null), 2500);
  };

  const formatDateTimeDE = (value?: string) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(d);
  };

  useEffect(() => {
    setForm(settings);
    setFaciliooCreateParentGroupId(String(settings.management.faciliooDocumentGroupId ?? "").trim() || FACILIOO_GROUP_NONE);
    setFaciliooCreateGroupName("");
  }, [settings]);

  useEffect(() => {
    if (isPlatformSettingsHost) return;
    let cancelled = false;
    const loadFaciliooWebhookStatus = async () => {
      setFaciliooWebhookLoading(true);
      try {
        const result = await api.getFaciliooWebhookTarget();
        if (!cancelled) {
          setFaciliooWebhookStatus(result);
          setFaciliooWebhookFeedback(null);
        }
      } catch {
        if (!cancelled) {
          setFaciliooWebhookStatus(null);
          setFaciliooWebhookFeedback({
            type: "error",
            text: "Webhook-Konfiguration konnte nicht geladen werden."
          });
        }
      } finally {
        if (!cancelled) setFaciliooWebhookLoading(false);
      }
    };
    void loadFaciliooWebhookStatus();
    return () => {
      cancelled = true;
    };
  }, [isPlatformSettingsHost]);

  useEffect(() => {
    if (isPlatformSettingsHost) return;
    if (form.management.faciliooEnabled === false) return;
    let cancelled = false;
    const loadDocumentGroups = async () => {
      setFaciliooDocumentGroupsLoading(true);
      try {
        const result: FaciliooDocumentGroupsResult = await api.getFaciliooDocumentGroups();
        if (!cancelled) setFaciliooDocumentGroups(result.data ?? []);
      } catch {
        if (!cancelled) setFaciliooDocumentGroups([]);
      } finally {
        if (!cancelled) setFaciliooDocumentGroupsLoading(false);
      }
    };
    void loadDocumentGroups();
    return () => {
      cancelled = true;
    };
  }, [isPlatformSettingsHost, form.management.faciliooEnabled]);

  useEffect(() => {
    let cancelled = false;
    const loadLetterheads = async () => {
      try {
        const result = await api.documentTemplates();
        if (!cancelled) {
          setLetterheads(result.letterheads ?? {});
        }
      } catch {
        if (!cancelled) {
          setLetterheads({});
        }
      }
    };
    void loadLetterheads();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadMicrosoftStatus = async () => {
      try {
        const result = await api.getMicrosoftMailStatus();
        if (!cancelled) setMicrosoftMailStatus(result);
      } catch {
        if (!cancelled) setMicrosoftMailStatus(null);
      }
    };
    void loadMicrosoftStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMicrosoftNdrFolders = async (options?: { mailbox?: string; silent?: boolean }) => {
    const mailbox = String(options?.mailbox ?? form.mail.microsoftSharedMailboxEmail ?? "").trim();
    if (!options?.silent) {
      setMicrosoftNdrFoldersLoading(true);
    }
    setMicrosoftNdrFoldersError("");
    try {
      const result: MicrosoftMailFoldersResult = await api.getMicrosoftMailFolders(mailbox || undefined);
      setMicrosoftNdrFolders(result.data ?? []);
      setMicrosoftNdrFoldersMailbox(String(result.mailbox ?? "").trim());
    } catch (error) {
      const message = error instanceof Error ? error.message : "Microsoft Ordner konnten nicht geladen werden.";
      setMicrosoftNdrFoldersError(message);
      setMicrosoftNdrFolders([]);
      setMicrosoftNdrFoldersMailbox("");
    } finally {
      if (!options?.silent) {
        setMicrosoftNdrFoldersLoading(false);
      }
    }
  };

  useEffect(() => {
    if (form.mail.provider !== "microsoft_graph") return;
    if (!microsoftMailStatus?.connected) return;
    void loadMicrosoftNdrFolders({ silent: true });
  }, [form.mail.provider, microsoftMailStatus?.connected]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const state = url.searchParams.get("m365");
    const msg = url.searchParams.get("m365_msg");
    if (!state) return;

    if (state === "connected") {
      setMailTestStatus({ type: "ok", text: "Microsoft 365 erfolgreich verbunden." });
      void api.getMicrosoftMailStatus().then((result) => setMicrosoftMailStatus(result)).catch(() => undefined);
    } else if (state === "error") {
      setMailTestStatus({ type: "error", text: msg || "Microsoft-Verbindung konnte nicht hergestellt werden." });
    }

    url.searchParams.delete("m365");
    url.searchParams.delete("m365_msg");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);


  const onLogoFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      setForm((prev) => ({ ...prev, logoDataUrl: result || undefined }));
    };
    reader.readAsDataURL(file);
  };

  const onOwnerLogoFile = (file: File | null) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      setForm((prev) => ({ ...prev, ownerLogoDataUrl: result || undefined }));
    };
    reader.readAsDataURL(file);
  };

  const onLogoRemove = () => {
    setForm((prev) => ({ ...prev, logoDataUrl: undefined }));
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const onOwnerLogoRemove = () => {
    setForm((prev) => ({ ...prev, ownerLogoDataUrl: undefined }));
    if (ownerLogoInputRef.current) {
      ownerLogoInputRef.current.value = "";
    }
  };

  const refreshLetterheads = async () => {
    const result = await api.documentTemplates();
    setLetterheads(result.letterheads ?? {});
  };

  const getLetterheadSlotLabel = (slot: LetterheadSlot) => {
    switch (slot) {
      case "digital":
        return "Briefpapier digital (Seite 1)";
      case "analog":
        return "Briefpapier analog (Seite 1)";
      case "digital_followup":
        return "Briefpapier digital (ab Seite 2)";
      case "analog_followup":
        return "Briefpapier analog (ab Seite 2)";
      default:
        return "Briefpapier";
    }
  };

  const onLetterheadUpload = async (kanal: LetterheadSlot, file: File | null) => {
    if (!file) return;
    setLetterheadPending(kanal);
    try {
      await api.uploadDocumentLetterhead(kanal, file);
      await refreshLetterheads();
      showSettingsActionFeedback({
        type: "ok",
        text: `${getLetterheadSlotLabel(kanal)} hochgeladen.`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload fehlgeschlagen";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setLetterheadPending(null);
      if (kanal === "digital" && letterheadDigitalInputRef.current) {
        letterheadDigitalInputRef.current.value = "";
      }
      if (kanal === "analog" && letterheadAnalogInputRef.current) {
        letterheadAnalogInputRef.current.value = "";
      }
      if (kanal === "digital_followup" && letterheadDigitalFollowupInputRef.current) {
        letterheadDigitalFollowupInputRef.current.value = "";
      }
      if (kanal === "analog_followup" && letterheadAnalogFollowupInputRef.current) {
        letterheadAnalogFollowupInputRef.current.value = "";
      }
    }
  };

  const onLetterheadDelete = async (kanal: LetterheadSlot) => {
    setLetterheadDeletePending(kanal);
    try {
      await api.deleteDocumentLetterhead(kanal);
      await refreshLetterheads();
      showSettingsActionFeedback({
        type: "ok",
        text: `${getLetterheadSlotLabel(kanal)} gelöscht.`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Löschen fehlgeschlagen";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setLetterheadDeletePending(null);
    }
  };

  const getLetterheadMargins = (slot: LetterheadSlot) => {
    const entry = letterheads[slot];
    return {
      topMarginMm: Number.isFinite(Number(entry?.topMarginMm)) ? Number(entry?.topMarginMm) : 25.4,
      bottomMarginMm: Number.isFinite(Number(entry?.bottomMarginMm)) ? Number(entry?.bottomMarginMm) : 22.6
    };
  };

  const getLetterheadFirstPageLayout = (slot: "digital" | "analog") => {
    const entry = letterheads[slot];
    const margins = getLetterheadMargins(slot);
    const defaultRecipientTopMm = Number((margins.topMarginMm + 7.1).toFixed(1));
    const defaultBodyStartMm = Number((margins.topMarginMm + 74.1).toFixed(1));
    return {
      firstPageRecipientTopMm: Number.isFinite(Number(entry?.firstPageRecipientTopMm))
        ? Number(entry?.firstPageRecipientTopMm)
        : defaultRecipientTopMm,
      firstPageBodyStartMm: Number.isFinite(Number(entry?.firstPageBodyStartMm))
        ? Number(entry?.firstPageBodyStartMm)
        : defaultBodyStartMm
    };
  };

  const onLetterheadMarginsChange = (slot: LetterheadSlot, field: "topMarginMm" | "bottomMarginMm", value: string) => {
    const raw = Number(value);
    const nextValue = Number.isFinite(raw) ? Math.max(0, Math.min(60, raw)) : 0;
    setLetterheads((prev) => {
      const existing = prev[slot];
      const margins = {
        topMarginMm: Number.isFinite(Number(existing?.topMarginMm)) ? Number(existing?.topMarginMm) : 25.4,
        bottomMarginMm: Number.isFinite(Number(existing?.bottomMarginMm)) ? Number(existing?.bottomMarginMm) : 22.6
      };
      return {
        ...prev,
        [slot]: {
          ...existing,
          topMarginMm: field === "topMarginMm" ? nextValue : margins.topMarginMm,
          bottomMarginMm: field === "bottomMarginMm" ? nextValue : margins.bottomMarginMm
        }
      };
    });
  };

  const onLetterheadMarginsSave = async (slot: LetterheadSlot) => {
    const margins = getLetterheadMargins(slot);
    setLetterheadMarginsPending(slot);
    try {
      await api.updateDocumentLetterheadMargins(slot, margins);
      await refreshLetterheads();
      showSettingsActionFeedback({
        type: "ok",
        text: `${getLetterheadSlotLabel(slot)} Ränder gespeichert.`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Speichern fehlgeschlagen";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setLetterheadMarginsPending(null);
    }
  };

  const onLetterheadFirstPageLayoutChange = (
    slot: "digital" | "analog",
    field: "firstPageRecipientTopMm" | "firstPageBodyStartMm",
    value: string
  ) => {
    const raw = Number(value);
    const nextValue = Number.isFinite(raw) ? Math.max(0, Math.min(260, raw)) : 0;
    setLetterheads((prev) => {
      const existing = prev[slot];
      const layout = getLetterheadFirstPageLayout(slot);
      return {
        ...prev,
        [slot]: {
          ...existing,
          firstPageRecipientTopMm: field === "firstPageRecipientTopMm" ? nextValue : layout.firstPageRecipientTopMm,
          firstPageBodyStartMm: field === "firstPageBodyStartMm" ? nextValue : layout.firstPageBodyStartMm
        }
      };
    });
  };

  const onLetterheadFirstPageLayoutSave = async (slot: "digital" | "analog") => {
    const payload = getLetterheadFirstPageLayout(slot);
    setLetterheadFirstPageLayoutPending(slot);
    try {
      await api.updateDocumentLetterheadFirstPageLayout(slot, payload);
      await refreshLetterheads();
      showSettingsActionFeedback({
        type: "ok",
        text: `${getLetterheadSlotLabel(slot)} Erstseiten-Layout gespeichert.`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Speichern fehlgeschlagen";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setLetterheadFirstPageLayoutPending(null);
    }
  };
  const onReminderDaysChange = (key: "firstReminderDays" | "secondReminderDays", value: number) => {
    const normalized = Number.isFinite(value) ? Math.max(1, Math.min(365, Math.round(value))) : 1;
    setForm((prev) => ({
      ...prev,
      reminders: {
        ...prev.reminders,
        [key]: normalized
      }
    }));
  };

  const onUploadDragOver = (event: React.DragEvent<HTMLDivElement>, target: Exclude<UploadDropTarget, null>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (uploadDropTarget !== target) {
      setUploadDropTarget(target);
    }
  };

  const onUploadDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setUploadDropTarget((prev) => (prev ? null : prev));
  };

  const onUploadDrop = (event: React.DragEvent<HTMLDivElement>, target: Exclude<UploadDropTarget, null>) => {
    event.preventDefault();
    setUploadDropTarget(null);
    const file = event.dataTransfer.files?.[0] ?? null;
    if (!file) return;
    if (target === "logo-backend") {
      onLogoFile(file);
      return;
    }
    if (target === "logo-owner") {
      onOwnerLogoFile(file);
      return;
    }
    if (target === "letterhead-digital") {
      void onLetterheadUpload("digital", file);
      return;
    }
    if (target === "letterhead-analog") {
      void onLetterheadUpload("analog", file);
      return;
    }
    if (target === "letterhead-digital-followup") {
      void onLetterheadUpload("digital_followup", file);
      return;
    }
    void onLetterheadUpload("analog_followup", file);
  };

  const updateFaciliooAnredeKeyTable = (rows: FaciliooKeyTableRow[]) => {
    setForm((prev) => ({
      ...prev,
      management: {
        ...prev.management,
        faciliooAnredeKeyTable: cleanKeyTableRows(rows, { includeBrief: true })
      }
    }));
  };

  const updateFaciliooTitelKeyTable = (rows: FaciliooKeyTableRow[]) => {
    setForm((prev) => ({
      ...prev,
      management: {
        ...prev.management,
        faciliooTitelKeyTable: cleanKeyTableRows(rows)
      }
    }));
  };

  const runMailTest = async () => {
    const recipientEmail = (form.mail.testRecipientEmail || "").trim();
    if (!recipientEmail) {
      setMailTestStatus({ type: "error", text: "Bitte Test-E-Mail-Adresse eintragen." });
      return;
    }
    if (form.mail.provider === "microsoft_graph" && !microsoftMailStatus?.connected) {
      setMailTestStatus({ type: "error", text: "Microsoft 365 ist noch nicht verbunden. Bitte zuerst verbinden." });
      return;
    }
    if (form.mail.provider === "smtp") {
      const port = Number(form.mail.smtpPort);
      const secure = Boolean(form.mail.smtpSecure);
      if ((port === 587 && secure) || (port === 465 && !secure)) {
        setMailTestStatus({
          type: "error",
          text: "SMTP-Kombination ungueltig. Bitte fuer 587 Secure=Nein oder fuer 465 Secure=Ja einstellen."
        });
        return;
      }
    }

    setMailTestPending(true);
    setMailTestStatus(null);
    try {
      const result = await api.testMailSettings(recipientEmail, form.mail);
      setMailTestStatus({ type: "ok", text: `Testversand erfolgreich über ${result.provider} (${result.messageId}).` });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Testversand fehlgeschlagen";
      setMailTestStatus({ type: "error", text: message });
    } finally {
      setMailTestPending(false);
    }
  };

  const regenerateFaciliooWebhookKey = async () => {
    setFaciliooWebhookRegenerating(true);
    setFaciliooWebhookGeneratedKey("");
    setShowFaciliooWebhookApiKey(false);
    setFaciliooWebhookFeedback(null);
    try {
      const result: FaciliooWebhookTargetRegenerateResult = await api.regenerateFaciliooWebhookTargetKey("default");
      setFaciliooWebhookStatus({
        ok: true,
        authorizationType: 4,
        targetUrl: result.targetUrl,
        endpointPath: result.endpointPath,
        hasCredential: true,
        credential: result.credential
      });
      setFaciliooWebhookGeneratedKey(result.apiKey);
      setFaciliooWebhookFeedback({
        type: "ok",
        text: "Webhook-API-Key wurde erfolgreich neu erzeugt."
      });
      showSettingsActionFeedback({ type: "ok", text: "Webhook-API-Key neu erzeugt. Bitte jetzt in Facilioo eintragen." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Webhook-API-Key konnte nicht erzeugt werden.";
      setFaciliooWebhookFeedback({ type: "error", text: message });
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setFaciliooWebhookRegenerating(false);
    }
  };

  const registerFaciliooWebhookAtFacilioo = async () => {
    setFaciliooWebhookRegistering(true);
    setFaciliooWebhookGeneratedKey("");
    setShowFaciliooWebhookApiKey(false);
    setFaciliooWebhookFeedback(null);
    try {
      const result: FaciliooWebhookTargetRegisterResult = await api.registerFaciliooWebhookTarget({
        replaceExisting: true,
        shouldGroup: false,
        shouldGroupContinueAfterFail: false,
        includePropertyCreated: true
      });
      setFaciliooWebhookStatus({
        ok: true,
        authorizationType: 4,
        targetUrl: result.targetUrl,
        endpointPath: result.endpointPath,
        hasCredential: true,
        credential: result.credential
      });
      setFaciliooWebhookGeneratedKey(result.apiKey);
      setFaciliooWebhookFeedback({
        type: "ok",
        text: `Webhook bei Facilioo registriert (ID: ${result.registration.id || "n/a"}).`
      });
      showSettingsActionFeedback({
        type: "ok",
        text: `Webhook bei Facilioo registriert (ID: ${result.registration.id || "n/a"}). Vorherige Registrierungen ersetzt: ${result.replaced.deletedIds.length}.`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Facilioo-Webhook-Registration fehlgeschlagen.";
      setFaciliooWebhookFeedback({ type: "error", text: message });
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setFaciliooWebhookRegistering(false);
    }
  };

  const connectMicrosoftMail = async () => {
    setMicrosoftMailPending(true);
    setMailTestStatus(null);
    try {
      const returnPath = typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/settings";
      const result = await api.connectMicrosoftMail(returnPath);
      if (typeof window !== "undefined") {
        window.location.href = result.authUrl;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Microsoft-Verbindung konnte nicht gestartet werden.";
      setMailTestStatus({ type: "error", text: message });
      setMicrosoftMailPending(false);
    }
  };

  const disconnectMicrosoftMail = async () => {
    setMicrosoftMailPending(true);
    setMailTestStatus(null);
    try {
      await api.disconnectMicrosoftMail();
      const refreshed = await api.getMicrosoftMailStatus();
      setMicrosoftMailStatus(refreshed);
      setMailTestStatus({ type: "ok", text: "Microsoft 365 Verbindung wurde getrennt." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Microsoft-Verbindung konnte nicht getrennt werden.";
      setMailTestStatus({ type: "error", text: message });
    } finally {
      setMicrosoftMailPending(false);
    }
  };

  const registerFaciliooWebhookUnitOnlyAtFacilioo = async () => {
    setFaciliooWebhookRegisteringUnitOnly(true);
    setFaciliooWebhookGeneratedKey("");
    setShowFaciliooWebhookApiKey(false);
    setFaciliooWebhookFeedback(null);
    try {
      const result: FaciliooWebhookTargetRegisterResult = await api.registerFaciliooWebhookTarget({
        replaceExisting: true,
        shouldGroup: false,
        shouldGroupContinueAfterFail: false,
        includePropertyCreated: false,
        enabledEvents: [
          { type: 1, entityName: "Unit" },
          { type: 2, entityName: "Unit" },
          { type: 3, entityName: "Unit" }
        ]
      });
      setFaciliooWebhookStatus({
        ok: true,
        authorizationType: 4,
        targetUrl: result.targetUrl,
        endpointPath: result.endpointPath,
        hasCredential: true,
        credential: result.credential
      });
      setFaciliooWebhookGeneratedKey(result.apiKey);
      setFaciliooWebhookFeedback({
        type: "ok",
        text: `Unit-only Webhook bei Facilioo registriert (ID: ${result.registration.id || "n/a"}).`
      });
      showSettingsActionFeedback({
        type: "ok",
        text: `Unit-only Webhook registriert (ID: ${result.registration.id || "n/a"}).`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Facilioo Unit-only Webhook-Registration fehlgeschlagen.";
      setFaciliooWebhookFeedback({ type: "error", text: message });
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setFaciliooWebhookRegisteringUnitOnly(false);
    }
  };

  const testFaciliooWebhookConnection = async () => {
    setFaciliooWebhookConnectionTesting(true);
    setFaciliooWebhookConnectionTest(null);
    setFaciliooWebhookPingResult(null);
    setFaciliooWebhookFeedback(null);
    try {
      const result = await api.testFaciliooWebhookConnection();
      setFaciliooWebhookConnectionTest(result);
      setFaciliooWebhookFeedback({
        type: result.ok ? "ok" : "error",
        text: result.ok
          ? "Verbindungstest erfolgreich: Outbound, Inbound und Sync sind plausibel."
          : "Verbindungstest hat Probleme gefunden. Details siehe Testausgabe unten."
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Facilioo-Verbindungstest fehlgeschlagen.";
      setFaciliooWebhookFeedback({ type: "error", text: message });
    } finally {
      setFaciliooWebhookConnectionTesting(false);
    }
  };

  const pingFaciliooWebhookConnection = async () => {
    setFaciliooWebhookPingTesting(true);
    setFaciliooWebhookPingResult(null);
    setFaciliooWebhookFeedback(null);
    try {
      const result = await api.pingFaciliooWebhookEndpoint();
      setFaciliooWebhookPingResult(result);
      setFaciliooWebhookFeedback({
        type: result.ok ? "ok" : "error",
        text: result.ok
          ? "Inbound-Ping erfolgreich: Endpoint, API-Key-Validierung, Parsing und Queue wurden lokal geprueft."
          : "Inbound-Ping fehlgeschlagen. Details siehe Testausgabe unten."
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Inbound-Ping fehlgeschlagen.";
      setFaciliooWebhookFeedback({ type: "error", text: message });
    } finally {
      setFaciliooWebhookPingTesting(false);
    }
  };

  const ensureFaciliooDocumentGroup = async () => {
    setFaciliooDocumentGroupCreating(true);
    try {
      const baseGroupName = String(faciliooCreateGroupName ?? "").trim();
      const hasParent = faciliooCreateParentGroupId !== FACILIOO_GROUP_NONE;
      const selectedParent = faciliooDocumentGroups.find(
        (group) => group.groupId === String(faciliooCreateParentGroupId ?? "").trim()
      );
      const resolvedParentName = String(selectedParent?.groupName ?? "").trim();
      const newLeafName = String(baseGroupName).trim();
      const groupName = hasParent
        ? resolvedParentName && newLeafName
          ? `${resolvedParentName} / ${newLeafName}`
          : newLeafName
        : newLeafName;
      if (!groupName) {
        showSettingsActionFeedback({ type: "info", text: "Bitte einen Namen für die neue Gruppe eingeben." });
        setFaciliooDocumentGroupCreating(false);
        return;
      }
      const result: FaciliooEnsureDocumentGroupResult = await api.ensureFaciliooDocumentGroup({
        groupName: groupName || undefined,
        forceCreate: true,
        parentGroupId: hasParent ? String(faciliooCreateParentGroupId).trim() : undefined
      });
      setForm((prev) => ({
        ...prev,
        management: {
          ...prev.management,
          faciliooDocumentGroupId: String(result.groupId ?? "").trim(),
          faciliooDocumentGroupName: String(result.groupName ?? prev.management.faciliooDocumentGroupName ?? "").trim()
        }
      }));
      setFaciliooCreateParentGroupId(String(result.groupId ?? "").trim() || FACILIOO_GROUP_NONE);
      showSettingsActionFeedback({
        type: "ok",
        text: result.created
          ? `Dokumentgruppe "${result.groupName}" angelegt (ID: ${result.groupId}).`
          : `Dokumentgruppe "${result.groupName}" bereits vorhanden (ID: ${result.groupId}).`
      });
      setFaciliooCreateGroupName("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Dokumentgruppe konnte nicht angelegt werden.";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setFaciliooDocumentGroupCreating(false);
    }
  };

  const resetFaciliooDocumentGroup = async () => {
    setFaciliooDocumentGroupResetting(true);
    try {
      const result: FaciliooResetDocumentGroupResult = await api.resetFaciliooDocumentGroup();
      setForm((prev) => ({
        ...prev,
        management: {
          ...prev.management,
          faciliooDocumentGroupId: "",
          faciliooDocumentGroupName: String(result.groupName ?? prev.management.faciliooDocumentGroupName ?? "").trim()
        }
      }));
      setFaciliooCreateParentGroupId(FACILIOO_GROUP_NONE);
      setFaciliooCreateGroupName("");
      showSettingsActionFeedback({
        type: "ok",
        text: "Facilioo Dokumentgruppe vollständig zurückgesetzt."
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Dokumentgruppe konnte nicht zurückgesetzt werden.";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setFaciliooDocumentGroupResetting(false);
    }
  };

  const refreshFaciliooDocumentGroups = async () => {
    setFaciliooDocumentGroupsLoading(true);
    try {
      const result: FaciliooDocumentGroupsResult = await api.getFaciliooDocumentGroups();
      setFaciliooDocumentGroups(result.data ?? []);
      showSettingsActionFeedback({ type: "ok", text: "Facilioo Dokumentgruppen aktualisiert." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Dokumentgruppen konnten nicht geladen werden.";
      showSettingsActionFeedback({ type: "info", text: message });
    } finally {
      setFaciliooDocumentGroupsLoading(false);
    }
  };


  const dataClearTypeLabel: Record<DataInventoryType, string> = {
    objects: "Objekte (inkl. abhängiger Daten)",
    units: "Einheiten",
    owners: "Eigentümer",
    assignments: "Zuordnungen",
    resolutions: "Beschlüsse",
    all: "Alles"
  };

  const mailProviderOptions: Array<{
    value: UiSettings["mail"]["provider"];
    label: string;
    description: string;
  }> = [
    {
      value: "microsoft_graph",
      label: "Microsoft 365 (OAuth, Standard)",
      description: "OAuth Versand über Microsoft 365"
    },
    {
      value: "smtp",
      label: "SMTP (Benutzerdefiniert)",
      description: "Eigener SMTP-Server"
    },
    {
      value: "brevo_api",
      label: "Brevo API",
      description: "Versand über Brevo API"
    }
  ];
  const selectedMailProvider = mailProviderOptions.find((option) => option.value === form.mail.provider) ?? mailProviderOptions[0];
  const updateMailProvider = (provider: UiSettings["mail"]["provider"]) => {
    setForm((prev) => ({ ...prev, mail: { ...prev.mail, provider } }));
    setMailProviderMenuOpen(false);
    setMailProviderMenuHovered(false);
  };

  useEffect(() => {
    if (!mailProviderMenuOpen || mailProviderMenuHovered) return;
    const timeoutId = window.setTimeout(() => {
      setMailProviderMenuOpen(false);
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [mailProviderMenuHovered, mailProviderMenuOpen]);

  const renderDesignColorField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    hint: string,
    disabled = false
  ) => (
    <div className="form-field settings-color-field settings-v2-design-color-field">
      <label>{label}</label>
      <div className="settings-color-row settings-v2-design-color-row">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
        <input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
      </div>
      <small className="settings-upload-hint">{hint}</small>
    </div>
  );

  const renderDesignToggleField = (label: string, checked: boolean, onChange: (checked: boolean) => void) => (
    <div className="form-field settings-color-field settings-v2-design-color-field">
      <label>{label}</label>
      <label className="settings-toggle" aria-label={label}>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="settings-toggle-slider" />
      </label>
    </div>
  );

  const tabs: Array<{ key: SettingsTabKey; label: string }> = [
    { key: "allgemein", label: "Allgemein" },
    { key: "facilioo", label: "Facilioo" },
    { key: "mailversand", label: "Mailversand" },
    { key: "design", label: "Design" },
    { key: "uploads", label: "Uploads" },
    { key: "datenbestand", label: "Datenbestand" }
  ];

  const runClearDataByType = async () => {
    setDataClearPending(true);
    setDataClearStatus(null);
    try {
      const result = await api.clearDataByType(dataClearType);
      setDataClearStatus({
        type: "ok",
        text: `${result.message} Gelöscht: Objekte ${result.deleted.objects}, Einheiten ${result.deleted.units}, Eigentümer ${result.deleted.owners}, Zuordnungen ${result.deleted.assignments}, Beschlüsse ${result.deleted.resolutions}.`
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Löschvorgang fehlgeschlagen";
      setDataClearStatus({ type: "error", text: message });
    } finally {
      setDataClearPending(false);
    }
  };
  return (
    <div className="resolutions-v2-page settings-v2-page">
      <section className="page-header panel resolutions-v2-page-header object-detail-v2-header settings-v2-header">
        <h2 className="resolutions-v2-title object-detail-v2-title" style={{ margin: 0 }}>
          <span className="resolutions-v2-title-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 7h14M5 12h14M5 17h14" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              <circle cx="9" cy="7" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="15" cy="12" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="11" cy="17" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </span>
          <span className="resolutions-v2-title-copy">
            <span className="resolutions-v2-title-main">Einstellungen</span>
            <span className="resolutions-v2-title-sub">Mandant, Integrationen, Versand und Darstellung</span>
          </span>
        </h2>
        <div className="form-actions settings-v2-header-actions" style={{ marginTop: 0 }}>
          <button
            className={`settings-save-btn resolutions-v2-create-btn settings-v2-action-btn settings-v2-action-btn--primary${saveButtonFlash ? " is-flash" : ""}`}
            onClick={() => {
              const first = Math.max(1, Math.min(365, Math.round(form.reminders.firstReminderDays || 1)));
              const secondRaw = Math.max(1, Math.min(365, Math.round(form.reminders.secondReminderDays || 1)));
              const second = secondRaw >= first ? Math.max(1, first - 1) : secondRaw;
              onChange({
                ...form,
                reminders: {
                  firstReminderDays: first,
                  secondReminderDays: second
                }
              });
              setSaveButtonFlash(true);
              window.setTimeout(() => setSaveButtonFlash(false), 700);
            }}
          >
            <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M4.4 4.4h9.1l2.1 2.1v9.1H4.4z" stroke="currentColor" strokeWidth="1.7" />
                <path d="M7 4.4v4h6v-4M7.4 15h5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </span>
            <span>Einstellungen speichern</span>
          </button>
          <button
            type="button"
            className="settings-v2-action-btn settings-v2-action-btn--secondary"
            onClick={() => {
              setForm(defaultUiSettings);
            }}
          >
            Zurücksetzen
          </button>
        </div>
        {settingsActionFeedback || saveStatus ? (
          <small className={`settings-action-feedback ${(settingsActionFeedback ?? saveStatus)?.type}`}>
            {(settingsActionFeedback ?? saveStatus)?.text}
          </small>
        ) : null}
      </section>

      <section className="panel object-tabbar object-tabbar-v2 settings-v2-tabbar">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`object-tabbtn object-tabbtn-v2 ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </section>

      {tab === "allgemein" && !isPlatformSettingsHost ? <section className="panel create-form settings-section settings-v2-section settings-v2-general-card">
        <div className="settings-v2-card-head">
          <span className="settings-v2-card-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M6.2 19.2V5.4h8.7v13.8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M14.9 9.2h3.2v10" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M8.8 8.2h1.1M11.5 8.2h1.1M8.8 11.5h1.1M11.5 11.5h1.1M8.8 14.8h1.1M11.5 14.8h1.1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M4.7 19.2h14.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <div className="settings-v2-card-title">
            <h3>Hausverwaltungsdaten</h3>
            <p>Diese Daten werden aus dem Mandantenprofil übernommen und können hier nicht bearbeitet werden.</p>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-field">
            <label>Firmenname</label>
            <input value={form.management.firmenname} disabled readOnly />
          </div>
          <div className="form-field">
            <label>Straße</label>
            <input value={form.management.strasse} disabled readOnly />
          </div>
          <div className="form-field">
            <label>PLZ</label>
            <input value={form.management.plz} disabled readOnly />
          </div>
          <div className="form-field">
            <label>Ort</label>
            <input value={form.management.ort} disabled readOnly />
          </div>
          <div className="form-field">
            <label>E-Mail</label>
            <input value={form.management.email} disabled readOnly />
          </div>
          <div className="form-field">
            <label>Telefon</label>
            <input value={form.management.telefon} disabled readOnly />
          </div>
        </div>
      </section> : null}

      {tab === "facilioo" && !isPlatformSettingsHost ? <section className="panel create-form settings-section settings-v2-section settings-v2-general-card settings-v2-facilioo-card">
        <div className="settings-v2-card-head settings-v2-facilioo-head">
          <div className="settings-v2-facilioo-title-row">
            <span className="settings-v2-card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9.8 14.2 14.2 9.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M10.8 7.1 12 5.9a4 4 0 0 1 5.7 5.7l-1.2 1.2M13.2 16.9 12 18.1a4 4 0 1 1-5.7-5.7l1.2-1.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="settings-v2-card-title">
              <h3>Facilioo</h3>
              <p>Konfigurieren Sie die Facilioo-Integration und die Zuordnung der Datenfelder.</p>
            </div>
          </div>
          <div className="settings-facilioo-toggle-wrap settings-v2-facilioo-toggle">
            <label className="settings-toggle" aria-label="Facilioo aktivieren oder deaktivieren">
              <input
                type="checkbox"
                checked={form.management.faciliooEnabled !== false}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    management: {
                      ...prev.management,
                      faciliooEnabled: e.target.checked
                    }
                  }))
                }
              />
              <span className="settings-toggle-slider" />
            </label>
            <span className="settings-facilioo-state">
              Facilioo-Funktionen {form.management.faciliooEnabled !== false ? "aktiv" : "deaktiviert"}
            </span>
          </div>
        </div>
        <div className="form-grid settings-v2-facilioo-grid">
          {form.management.faciliooEnabled !== false ? (
            <>
          <div className="form-field">
            <label>Verwaltungs-ID Facilioo <span className="settings-v2-label-info" title="Mandanten- oder Verwaltungs-ID aus Facilioo">i</span></label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.management.verwaltungsIdFacilioo}
              onChange={(e) => setForm({ ...form, management: { ...form.management, verwaltungsIdFacilioo: e.target.value } })}
            />
          </div>
          <div className="form-field">
            <label>Trennzeichen Straße (für Straße 2) <span className="settings-v2-label-info" title="Trennzeichen zum Aufteilen zusätzlicher Straßenangaben">i</span></label>
            <input
              value={form.management.faciliooStrasseTrennzeichen}
              placeholder="|"
              onChange={(e) => setForm({ ...form, management: { ...form.management, faciliooStrasseTrennzeichen: e.target.value } })}
            />
          </div>
          <div className="form-field">
            <label>MEA Attributname(n) <span className="settings-v2-label-info" title="Facilioo-Attributnamen für MEA-Werte">i</span></label>
            <input
              value={form.management.faciliooMeaAttributeNames}
              placeholder="MEA Objekt, MEA"
              onChange={(e) =>
                setForm({ ...form, management: { ...form.management, faciliooMeaAttributeNames: e.target.value } })
              }
            />
          </div>
          <div className="form-field">
            <label>Stimmrechte Attributname(n) <span className="settings-v2-label-info" title="Facilioo-Attributnamen für Stimmrechte">i</span></label>
            <input
              value={form.management.faciliooStimmrechteAttributeNames}
              placeholder="Stimmrechte"
              onChange={(e) =>
                setForm({
                  ...form,
                  management: { ...form.management, faciliooStimmrechteAttributeNames: e.target.value }
                })
              }
            />
          </div>
          <div className="form-field">
            <label>Eigentümer-Zuordnungen importieren <span className="settings-v2-label-info" title="Umfang der importierten Eigentümer-Zuordnungen">i</span></label>
            <select
              value={form.management.faciliooOwnerImportScope === "all" ? "all" : "current"}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  management: {
                    ...prev.management,
                    faciliooOwnerImportScope: e.target.value === "all" ? "all" : "current"
                  }
                }))
              }
            >
              <option value="current">Nur aktuelle (ab heute gültige)</option>
              <option value="all">Alle (inkl. Vergangenheit)</option>
            </select>
          </div>
          <div className="form-field">
            <label>Objektfilter (kommagetrennt) <span className="settings-v2-label-info" title="Optionale Facilioo-Objekttypen als Filter">i</span></label>
            <input
              value={form.management.faciliooObjectTypeFilter ?? ""}
              placeholder="z. B. WEG, GdWE"
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  management: {
                    ...prev.management,
                    faciliooObjectTypeFilter: e.target.value
                  }
                }))
              }
            />
            <small className="settings-upload-hint">Leer lassen = Vollimport ohne Objektfilter.</small>
          </div>
          <div className="form-field form-field-wide">
            <button
              type="button"
              className="settings-collapsible-toggle settings-v2-collapsible-card"
              aria-expanded={showFaciliooKeyTables}
              onClick={() => setShowFaciliooKeyTables((prev) => !prev)}
            >
              <span className="settings-v2-card-icon settings-v2-card-icon--compact" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5.5 7.2h13M5.5 12h13M5.5 16.8h13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <circle cx="9" cy="7.2" r="1.9" fill="#fff" stroke="currentColor" strokeWidth="1.7" />
                  <circle cx="15" cy="12" r="1.9" fill="#fff" stroke="currentColor" strokeWidth="1.7" />
                  <circle cx="11" cy="16.8" r="1.9" fill="#fff" stroke="currentColor" strokeWidth="1.7" />
                </svg>
              </span>
              <span className="settings-v2-collapsible-copy">
                <strong>Schlüsseltabellen</strong>
                <small>Verwalten Sie die Zuordnung der Facilioo-Felder zu unseren Systemfeldern.</small>
              </span>
              <span className="settings-v2-collapsible-arrow" aria-hidden="true">{showFaciliooKeyTables ? "⌄" : "›"}</span>
            </button>
          </div>
          {showFaciliooKeyTables ? (
            <>
              <div className="form-field form-field-wide settings-management-box">
                <div className="settings-v2-keytable-title-row">
                  <div>
                    <h4>Anrede-Schlüsseltabelle</h4>
                    <small className="settings-upload-hint">
                      Spalte Definition = APP-Wert im Dropdown, Spalte Mapping = Facilioo-Suchbegriffe (Komma-getrennt).
                      Briefanrede 1 und 2 werden direkt aus der Schluesseltabelle gezogen.
                    </small>
                  </div>
                  <button
                    type="button"
                    className="settings-keytable-add settings-v2-keytable-add"
                    onClick={() =>
                      updateFaciliooAnredeKeyTable([
                        ...form.management.faciliooAnredeKeyTable,
                        { definition: "", mapping: "" }
                      ])
                    }
                  >
                    <span>Zeile hinzufügen</span>
                    <span aria-hidden="true">+</span>
                  </button>
                </div>
                <div className="settings-keytable settings-keytable-anrede">
                  <div className="settings-keytable-row settings-keytable-head">
                    <span>Definition</span>
                    <span>Mapping</span>
                    <span>Briefanrede 1</span>
                    <span>Briefanrede 2</span>
                    <span>Aktion</span>
                  </div>
                  {form.management.faciliooAnredeKeyTable.map((row, index) => (
                    <div className="settings-keytable-row" key={`anrede-row-${index}`}>
                      <input
                        value={row.definition}
                        placeholder="z. B. Frau / Herrn"
                        onChange={(e) => {
                          const next = [...form.management.faciliooAnredeKeyTable];
                          next[index] = { ...next[index], definition: e.target.value };
                          updateFaciliooAnredeKeyTable(next);
                        }}
                      />
                      <input
                        value={row.mapping}
                        placeholder="z. B. frau/herr, frau und herr, herr und frau"
                        onChange={(e) => {
                          const next = [...form.management.faciliooAnredeKeyTable];
                          next[index] = { ...next[index], mapping: e.target.value };
                          updateFaciliooAnredeKeyTable(next);
                        }}
                      />
                      <input
                        value={row.briefanrede_1 ?? ""}
                        placeholder="z. B. Sehr geehrte Frau"
                        onChange={(e) => {
                          const next = [...form.management.faciliooAnredeKeyTable];
                          next[index] = { ...next[index], briefanrede_1: e.target.value };
                          updateFaciliooAnredeKeyTable(next);
                        }}
                      />
                      <input
                        value={row.briefanrede_2 ?? ""}
                        placeholder="z. B. Sehr geehrter Herr"
                        onChange={(e) => {
                          const next = [...form.management.faciliooAnredeKeyTable];
                          next[index] = { ...next[index], briefanrede_2: e.target.value };
                          updateFaciliooAnredeKeyTable(next);
                        }}
                      />
                      <button
                        type="button"
                        className="settings-v2-keytable-delete"
                        aria-label="Zeile entfernen"
                        title="Zeile entfernen"
                        onClick={() =>
                          updateFaciliooAnredeKeyTable(
                            form.management.faciliooAnredeKeyTable.filter((_, rowIndex) => rowIndex !== index)
                          )
                        }
                      >
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M6.5 7.6v7.1M10 7.6v7.1M13.5 7.6v7.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                          <path d="M4.2 5.2h11.6M8 5.2V3.8h4v1.4M5.7 5.2l.7 11h7.2l.7-11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-field form-field-wide settings-management-box">
                <div className="settings-v2-keytable-title-row">
                  <div>
                    <h4>Titel-Schlüsseltabelle</h4>
                    <small className="settings-upload-hint">Spalte Definition = Ziel-Titel, Spalte Mapping = Facilioo-Titelbegriffe (Komma-getrennt).</small>
                  </div>
                  <button
                    type="button"
                    className="settings-keytable-add settings-v2-keytable-add"
                    onClick={() =>
                      updateFaciliooTitelKeyTable([
                        ...form.management.faciliooTitelKeyTable,
                        { definition: "", mapping: "" }
                      ])
                    }
                  >
                    <span>Zeile hinzufügen</span>
                    <span aria-hidden="true">+</span>
                  </button>
                </div>
                <div className="settings-keytable">
                  <div className="settings-keytable-row settings-keytable-head">
                    <span>Definition</span>
                    <span>Mapping</span>
                    <span>Aktion</span>
                  </div>
                  {form.management.faciliooTitelKeyTable.map((row, index) => (
                    <div className="settings-keytable-row" key={`titel-row-${index}`}>
                      <input
                        value={row.definition}
                        placeholder="z. B. Dr."
                        onChange={(e) => {
                          const next = [...form.management.faciliooTitelKeyTable];
                          next[index] = { ...next[index], definition: e.target.value };
                          updateFaciliooTitelKeyTable(next);
                        }}
                      />
                      <input
                        value={row.mapping}
                        placeholder="z. B. dr, dr."
                        onChange={(e) => {
                          const next = [...form.management.faciliooTitelKeyTable];
                          next[index] = { ...next[index], mapping: e.target.value };
                          updateFaciliooTitelKeyTable(next);
                        }}
                      />
                      <button
                        type="button"
                        className="settings-v2-keytable-delete"
                        aria-label="Zeile entfernen"
                        title="Zeile entfernen"
                        onClick={() =>
                          updateFaciliooTitelKeyTable(
                            form.management.faciliooTitelKeyTable.filter((_, rowIndex) => rowIndex !== index)
                          )
                        }
                      >
                        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M6.5 7.6v7.1M10 7.6v7.1M13.5 7.6v7.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                          <path d="M4.2 5.2h11.6M8 5.2V3.8h4v1.4M5.7 5.2l.7 11h7.2l.7-11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
          <div className="form-field form-field-wide">
            <button
              type="button"
              className="settings-collapsible-toggle settings-v2-collapsible-card"
              aria-expanded={showFaciliooApiTokenSection}
              onClick={() => setShowFaciliooApiTokenSection((prev) => !prev)}
            >
              <span className="settings-v2-card-icon settings-v2-card-icon--compact" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="5.2" y="4.9" width="13.6" height="14.2" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8.5 9.2h7M8.5 12h7M8.5 14.8h4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M15.2 14.4 17 16.2l2.8-3.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span className="settings-v2-collapsible-copy">
                <strong>API-Schlüssel</strong>
                <small>Verwalten Sie Ihren Facilioo API-Schlüssel für den sicheren Datenaustausch.</small>
              </span>
              <span className="settings-v2-collapsible-arrow" aria-hidden="true">{showFaciliooApiTokenSection ? "⌄" : "›"}</span>
            </button>
          </div>
          {showFaciliooApiTokenSection ? (
            <div className="form-field form-field-wide">
              <label>Facilioo API-Schlüssel / Bearer Token</label>
              <div className="settings-secret-row">
                <textarea
                  className={`settings-token-textarea settings-token-textarea-compact ${showFaciliooApiKey ? "" : "is-masked"}`.trim()}
                  rows={2}
                  autoComplete="off"
                  value={form.management.faciliooApiKey}
                  onChange={(e) => setForm({ ...form, management: { ...form.management, faciliooApiKey: e.target.value } })}
                />
                <button
                  type="button"
                  className="owner-auth-eye-btn"
                  onClick={() => setShowFaciliooApiKey((v) => !v)}
                  aria-label={showFaciliooApiKey ? "API-Schlüssel verbergen" : "API-Schlüssel anzeigen"}
                  title={showFaciliooApiKey ? "API-Schlüssel verbergen" : "API-Schlüssel anzeigen"}
                >
                  {showFaciliooApiKey ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="settings-facilioo-webhook-card" style={{ marginTop: 12 }}>
                <h4>Facilioo Dokumentgruppe</h4>
                <div className="settings-facilioo-docgroup-section">
                  <h5>Bestehende Dokumentgruppe auswählen</h5>
                  <div className="settings-facilioo-webhook-target-row settings-facilioo-docgroup-picker-row" style={{ gap: 8 }}>
                    <select
                      value={faciliooCreateParentGroupId}
                      onChange={(e) => {
                        const nextId = String(e.target.value || FACILIOO_GROUP_NONE).trim() || FACILIOO_GROUP_NONE;
                        const selectedGroup = faciliooDocumentGroupOptions.find((group) => group.groupId === nextId);
                        setFaciliooCreateParentGroupId(nextId);
                        setForm((prev) => ({
                          ...prev,
                          management: {
                            ...prev.management,
                            faciliooDocumentGroupId: nextId === FACILIOO_GROUP_NONE ? "" : nextId,
                            faciliooDocumentGroupName:
                              nextId === FACILIOO_GROUP_NONE
                                ? prev.management.faciliooDocumentGroupName
                                : String(selectedGroup?.groupName ?? prev.management.faciliooDocumentGroupName ?? "").trim()
                          }
                        }));
                      }}
                    >
                      <option value={FACILIOO_GROUP_NONE}>keine</option>
                      {faciliooDocumentGroupOptions.map((group) => (
                        <option key={group.groupId} value={group.groupId}>
                          {group.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="settings-facilioo-docgroup-refresh-btn settings-v2-inline-action"
                      onClick={() => void refreshFaciliooDocumentGroups()}
                      disabled={faciliooDocumentGroupsLoading}
                    >
                      <span className="settings-v2-inline-action-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M15.2 7.1A5.8 5.8 0 0 0 5 5.8L3.8 7.2M4.8 12.9A5.8 5.8 0 0 0 15 14.2l1.2-1.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M3.8 3.9v3.3h3.3M16.2 16.1v-3.3h-3.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>{faciliooDocumentGroupsLoading ? "Lade..." : "Liste aktualisieren"}</span>
                    </button>
                  </div>
                  <div className="settings-facilioo-webhook-target-row settings-facilioo-docgroup-id-row">
                    <div className="settings-facilioo-docgroup-current">
                      {(() => {
                        const currentId = String(faciliooCreateParentGroupId ?? "").trim();
                        if (!currentId || currentId === FACILIOO_GROUP_NONE) return "keine";
                        const selected = faciliooDocumentGroupOptions.find((group) => group.groupId === currentId);
                        const currentName = String(selected?.groupName ?? form.management.faciliooDocumentGroupName ?? "").trim();
                        return `${currentName || "Dokumentgruppe"} | ${currentId}`;
                      })()}
                    </div>
                  </div>
                </div>

                <div className="settings-facilioo-docgroup-section settings-facilioo-docgroup-create-section">
                  <h5>
                    {facliooSelectedParentHierarchy
                      ? `Neue Untergruppe in der Gruppe ${facliooSelectedParentHierarchy} anlegen`
                      : "Neue Dokumentengruppe anlegen"}
                  </h5>
                  <div className="settings-facilioo-webhook-target-row settings-facilioo-docgroup-create-row">
                    <input
                      value={faciliooCreateGroupName}
                      onChange={(e) => setFaciliooCreateGroupName(e.target.value)}
                      placeholder={
                        faciliooCreateParentGroupId === FACILIOO_GROUP_NONE
                          ? "Neue Gruppe (z.B. Umlaufbeschlüsse)"
                          : "Neue Untergruppe (z.B. 2026)"
                      }
                    />
                  </div>
                  <small className="settings-upload-hint">
                    Ist oben eine Gruppe ausgewählt, wird der eingegebene Name als Untergruppe unter dieser Hauptgruppe angelegt.
                  </small>
                  <small className="settings-upload-hint">
                    Auswahl "keine": es wird eine neue Gruppe angelegt. Bei ausgewählter Gruppe wird eine Untergruppe angelegt.
                  </small>
                  <div className="settings-logo-actions settings-facilioo-webhook-actions">
                    <button
                      type="button"
                      className="settings-v2-inline-action"
                      onClick={() => void ensureFaciliooDocumentGroup()}
                      disabled={faciliooDocumentGroupCreating || faciliooDocumentGroupResetting}
                    >
                      <span className="settings-v2-inline-action-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span>{faciliooDocumentGroupCreating ? "Lege an..." : "Dokumentgruppe in Facilioo anlegen"}</span>
                    </button>
                    <button
                      type="button"
                      className="settings-v2-inline-action"
                      onClick={() => void resetFaciliooDocumentGroup()}
                      disabled={faciliooDocumentGroupCreating || faciliooDocumentGroupResetting}
                    >
                      <span className="settings-v2-inline-action-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M6.5 7.6v7.1M10 7.6v7.1M13.5 7.6v7.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                          <path d="M4.2 5.2h11.6M8 5.2V3.8h4v1.4M5.7 5.2l.7 11h7.2l.7-11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span>{faciliooDocumentGroupResetting ? "Setze zurück..." : "Dokumentgruppe zurücksetzen"}</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="settings-facilioo-webhook-card">
                <h4>Facilioo Webhook Ziel (authorizationType 4)</h4>
                <div className="settings-facilioo-toggle-wrap" style={{ marginBottom: 10 }}>
                  <label className="settings-toggle" aria-label="Facilioo Webhook aktivieren oder deaktivieren">
                    <input
                      type="checkbox"
                      checked={form.management.faciliooWebhookEnabled !== false}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          management: {
                            ...prev.management,
                            faciliooWebhookEnabled: e.target.checked
                          }
                        }))
                      }
                    />
                    <span className="settings-toggle-slider" />
                  </label>
                  <span className="settings-facilioo-state">
                    Webhook-Verarbeitung {form.management.faciliooWebhookEnabled !== false ? "aktiv" : "deaktiviert"}
                  </span>
                </div>
                <div className="settings-facilioo-webhook-target-row">
                  <input
                    readOnly
                    value={faciliooWebhookStatus?.targetUrl ?? ""}
                    placeholder={faciliooWebhookLoading ? "Webhook endpoint wird geladen..." : "Webhook endpoint nicht verfuegbar"}
                  />
                  <button
                    type="button"
                    className="settings-v2-copy-button"
                    aria-label="Webhook Endpoint kopieren"
                    title="Webhook Endpoint kopieren"
                    disabled={!faciliooWebhookStatus?.targetUrl}
                    onClick={() => {
                      if (!faciliooWebhookStatus?.targetUrl) return;
                      void navigator.clipboard?.writeText(faciliooWebhookStatus.targetUrl);
                    }}
                  >
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                      <rect x="7" y="5" width="8.5" height="10.5" rx="1.4" stroke="currentColor" strokeWidth="1.6" />
                      <path d="M5 13V6.8A1.8 1.8 0 0 1 6.8 5H13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
                <small className="settings-upload-hint">
                  In Facilioo als targetUrl eintragen. Authentifizierung: authorizationType = 4 mit dem unten erzeugten API-Key.
                </small>
                {form.management.faciliooWebhookEnabled === false ? (
                  <small className="settings-upload-hint" style={{ color: "#b42318", fontWeight: 600 }}>
                    Webhook ist lokal deaktiviert: eingehende Events werden angenommen, aber nicht verarbeitet.
                  </small>
                ) : null}
                <div className="settings-facilioo-webhook-prefix">
                  <strong>Aktiver Key:</strong>
                  <span>
                    {faciliooWebhookStatus?.credential?.keyPrefix
                      ? `${faciliooWebhookStatus.credential.keyPrefix}...`
                      : faciliooWebhookStatus?.hasCredential
                        ? "vorhanden"
                        : "noch nicht erstellt"}
                  </span>
                </div>
                {faciliooWebhookFeedback ? (
                  <div
                    className={`settings-facilioo-webhook-feedback ${faciliooWebhookFeedback.type === "ok" ? "is-ok" : "is-error"}`}
                    role="status"
                  >
                    {faciliooWebhookFeedback.text}
                  </div>
                ) : null}
                {faciliooWebhookConnectionTest ? (
                  <div
                    className={`settings-facilioo-webhook-feedback ${faciliooWebhookConnectionTest.ok ? "is-ok" : "is-error"}`}
                    role="status"
                  >
                    <div>
                      <strong>Testzeitpunkt:</strong> {formatDateTimeDE(faciliooWebhookConnectionTest.checkedAt)}
                    </div>
                    <div>
                      <strong>Outbound:</strong> {faciliooWebhookConnectionTest.checks.outbound.ok ? "OK" : "Fehler"} -{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.message}
                    </div>
                    <div>
                      <strong>Inbound:</strong> {faciliooWebhookConnectionTest.checks.inbound.ok ? "OK" : "Fehler"} -{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.message}
                    </div>
                    <div>
                      <strong>Letzter erfolgreicher Inbound (2xx):</strong>{" "}
                      {formatDateTimeDE(faciliooWebhookConnectionTest.checks.inbound.lastSuccessfulInboundAt ?? undefined)}{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.lastSuccessfulInboundStatus
                        ? `(HTTP ${faciliooWebhookConnectionTest.checks.inbound.lastSuccessfulInboundStatus})`
                        : ""}
                    </div>
                    <div>
                      <strong>Erfolgreiche Event-ID / Webhook-ID:</strong>{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.lastSuccessfulEventId ?? "-"} /{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.lastSuccessfulWebhookId ?? "-"}
                    </div>
                    <div>
                      <strong>Letzter deduplizierter Event:</strong>{" "}
                      {formatDateTimeDE(faciliooWebhookConnectionTest.checks.inbound.lastDeduplicatedAt ?? undefined)}{" "}
                      ({faciliooWebhookConnectionTest.checks.inbound.lastDeduplicatedEventId ?? "-"} /{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.lastDeduplicatedWebhookId ?? "-"})
                    </div>
                    <div>
                      <strong>Letzter Reject (4xx/5xx):</strong> {formatDateTimeDE(faciliooWebhookConnectionTest.checks.inbound.lastRejectAt ?? undefined)}{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.lastRejectStatus
                        ? `(HTTP ${faciliooWebhookConnectionTest.checks.inbound.lastRejectStatus})`
                        : ""}
                    </div>
                    <div>
                      <strong>Reject Event-ID / Webhook-ID:</strong> {faciliooWebhookConnectionTest.checks.inbound.lastEventId ?? "-"} /{" "}
                      {faciliooWebhookConnectionTest.checks.inbound.lastRejectWebhookId ?? "-"}
                    </div>
                    <div>
                      <strong>Webhook-Registration-Events:</strong>{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.registrationValidation.hasAllExpectedEvents ? "vollstaendig" : "unvollstaendig"}{" "}
                      (registriert: {faciliooWebhookConnectionTest.checks.outbound.registrationValidation.registeredEvents.length}, erwartet:{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.registrationValidation.expectedEvents.length})
                    </div>
                    {faciliooWebhookConnectionTest.checks.outbound.registrationValidation.missingEvents.length ? (
                      <div>
                        <strong>Fehlende Events:</strong>{" "}
                        {faciliooWebhookConnectionTest.checks.outbound.registrationValidation.missingEvents
                          .map((event) => `${event.entityName}.${event.type}`)
                          .join(", ")}
                      </div>
                    ) : null}
                    <div>
                      <strong>Facilioo Deep-Check:</strong>{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.deepCheck.ok ? "OK" : "Hinweis"} -{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.deepCheck.message}
                    </div>
                    <div>
                      <strong>Events erzeugt:</strong> {faciliooWebhookConnectionTest.checks.outbound.deepCheck.matchingWebhooksCount} |{" "}
                      <strong>Zustellversuche:</strong> {faciliooWebhookConnectionTest.checks.outbound.deepCheck.matchingAttemptsCount}
                    </div>
                    <div>
                      <strong>Letzter Attempt-HTTP-Status:</strong>{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.deepCheck.latestAttempt?.responseStatusCode ?? "-"}
                    </div>
                    <div>
                      <strong>Letzter Attempt Webhook-ID:</strong>{" "}
                      {faciliooWebhookConnectionTest.checks.outbound.deepCheck.latestAttempt?.webhookId ?? "-"}
                    </div>
                    <div>
                      <strong>Sync:</strong> {faciliooWebhookConnectionTest.checks.sync.ok ? "OK" : "Fehler"} -{" "}
                      {faciliooWebhookConnectionTest.checks.sync.message}
                    </div>
                  </div>
                ) : null}
                {faciliooWebhookPingResult ? (
                  <div
                    className={`settings-facilioo-webhook-feedback ${faciliooWebhookPingResult.ok ? "is-ok" : "is-error"}`}
                    role="status"
                  >
                    <div>
                      <strong>Inbound-Ping:</strong> {faciliooWebhookPingResult.ok ? "OK" : "Fehler"} -{" "}
                      {faciliooWebhookPingResult.message}
                    </div>
                    <div>
                      <strong>Status:</strong> HTTP {faciliooWebhookPingResult.statusCode}
                    </div>
                    <div>
                      <strong>Event-ID / Webhook-ID:</strong> {faciliooWebhookPingResult.eventId} / {faciliooWebhookPingResult.webhookId}
                    </div>
                    <div>
                      <strong>API-Key Prefix:</strong> {faciliooWebhookPingResult.apiKeyPrefix ?? "-"}
                    </div>
                    <div>
                      <strong>Antwort:</strong> {faciliooWebhookPingResult.responseBody || "-"}
                    </div>
                  </div>
                ) : null}
                <div className="settings-logo-actions settings-facilioo-webhook-actions">
                  <button type="button" className="settings-v2-inline-action" onClick={() => void regenerateFaciliooWebhookKey()} disabled={faciliooWebhookRegenerating}>
                    <span className="settings-v2-inline-action-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M8.4 11.6 11.6 8.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        <path d="M8.9 6.2 10 5.1a3.3 3.3 0 0 1 4.7 4.7l-1.1 1.1M11.1 13.8 10 14.9a3.3 3.3 0 0 1-4.7-4.7l1.1-1.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span>
                      {faciliooWebhookRegenerating
                        ? "Erzeuge..."
                        : faciliooWebhookStatus?.hasCredential
                          ? "Webhook-Key neu erzeugen"
                          : "Webhook-Key erzeugen"}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="settings-v2-inline-action"
                    onClick={() => void registerFaciliooWebhookAtFacilioo()}
                    disabled={faciliooWebhookRegistering}
                    title="Erzeugt automatisch einen Webhook-Key, ersetzt alte Registrierungen für die gleiche targetUrl und legt die neue Registration bei Facilioo an."
                  >
                    <span className="settings-v2-inline-action-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M6.2 12.2a3.8 3.8 0 1 1 5.8-3.7h1.4a2.8 2.8 0 0 1 .2 5.6H7.1a2.2 2.2 0 0 1-.9-4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10 7.6v5M7.8 9.8 10 7.6l2.2 2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span>{faciliooWebhookRegistering ? "Registriere..." : "Bei Facilioo registrieren"}</span>
                  </button>
                  <button
                    type="button"
                    className="settings-v2-inline-action"
                    onClick={() => void testFaciliooWebhookConnection()}
                    disabled={faciliooWebhookConnectionTesting}
                    title="Prüft Facilioo-API, vorhandene Webhook-Registration, letzte eingehende Webhooks und Live-Sync-Zeitfenster."
                  >
                    <span className="settings-v2-inline-action-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M8.4 11.6 11.6 8.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        <path d="M8.9 6.2 10 5.1a3.3 3.3 0 0 1 4.7 4.7l-1.1 1.1M11.1 13.8 10 14.9a3.3 3.3 0 0 1-4.7-4.7l1.1-1.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span>{faciliooWebhookConnectionTesting ? "Prüfe..." : "Verbindung testen"}</span>
                  </button>
                  <button
                    type="button"
                    className="settings-v2-inline-action"
                    onClick={() => void pingFaciliooWebhookConnection()}
                    disabled={faciliooWebhookPingTesting}
                    title="Sendet einen lokalen Ping durch denselben Webhook-Endpoint zur Pruefung von Routing, API-Key-Validierung, Parsing und Queue."
                  >
                    <span className="settings-v2-inline-action-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M4.2 10h2.1m7.4 0h2.1M10 4.2v2.1m0 7.4v2.1M6.9 6.9l1.5 1.5m3.2 3.2 1.5 1.5M13.1 6.9l-1.5 1.5m-3.2 3.2-1.5 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </span>
                    <span>{faciliooWebhookPingTesting ? "Pinge..." : "Inbound-Ping testen"}</span>
                  </button>
                </div>
                {faciliooWebhookGeneratedKey ? (
                  <div className="settings-facilioo-webhook-generated">
                    <label>Neuer Webhook API-Key (einmalig anzeigen)</label>
                    <div className="settings-secret-row">
                      <textarea
                        className={`settings-token-textarea settings-token-textarea-compact ${showFaciliooWebhookApiKey ? "" : "is-masked"}`.trim()}
                        rows={2}
                        readOnly
                        value={faciliooWebhookGeneratedKey}
                      />
                      <button
                        type="button"
                        className="owner-auth-eye-btn"
                        onClick={() => setShowFaciliooWebhookApiKey((v) => !v)}
                        aria-label={showFaciliooWebhookApiKey ? "Webhook API-Key verbergen" : "Webhook API-Key anzeigen"}
                        title={showFaciliooWebhookApiKey ? "Webhook API-Key verbergen" : "Webhook API-Key anzeigen"}
                      >
                        {showFaciliooWebhookApiKey ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                            <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                              d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="form-field form-field-wide">
            <div className="imports-sync-info-banner v2-info-banner settings-v2-facilioo-info">
              <span className="imports-sync-info-icon" aria-hidden="true">i</span>
              <div className="imports-sync-info-copy">
                <strong>Hinweis</strong>
                <span>Änderungen an dieser Konfiguration werden erst nach dem Speichern aktiv.</span>
              </div>
            </div>
          </div>
            </>
          ) : (
            <div className="form-field form-field-wide">
              <small className="settings-upload-hint">
                Facilioo ist deaktiviert. Synchronisierung, Facilioo-Importe und Vorgangsnummern in der Beschlussanlage sind ausgeblendet.
              </small>
            </div>
          )}
        </div>
      </section> : null}
      {tab === "mailversand" ? <section className="panel create-form settings-section settings-v2-section settings-v2-mail-card">
        <div className="settings-v2-card-head settings-v2-mail-head">
          <span className="settings-v2-card-icon" aria-hidden="true">
            <MailEnvelopeIcon />
          </span>
          <div className="settings-v2-card-title">
            <h3>{form.mail.provider === "brevo_api" ? "Mailversand mit Brevo" : "Mailversand"}</h3>
            <p>
              {form.mail.provider === "brevo_api"
                ? "Verbinden Sie Ihr Brevo-Konto, um E-Mails über die Brevo API zu versenden."
                : form.mail.provider === "smtp"
                  ? "Konfigurieren Sie den SMTP-Server für den E-Mail-Versand."
                  : "Konfigurieren Sie den E-Mail Versand und die zugehörigen Einstellungen."}
            </p>
          </div>
          {form.mail.provider === "brevo_api" ? (
            <span className="settings-v2-mail-state-badge">Nicht verbunden</span>
          ) : null}
          {form.mail.provider === "smtp" ? (
            <button type="button" className="settings-v2-help-btn">
              <span aria-hidden="true">?</span>
              Hilfe zur Einrichtung
              <span className="settings-v2-help-chevron" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="m5.4 7.8 4.6 4.6 4.6-4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>
          ) : null}
        </div>
        <div className="form-grid settings-v2-mail-grid">
          <div className="form-field form-field-wide settings-v2-mail-panel settings-v2-mail-provider-panel">
            <label>
              Provider
              <span className="settings-v2-label-info" title="Versanddienst für ausgehende E-Mails">i</span>
            </label>
            <div
              className="settings-v2-provider-dropdown"
              onMouseEnter={() => setMailProviderMenuHovered(true)}
              onMouseLeave={() => setMailProviderMenuHovered(false)}
              onFocus={() => setMailProviderMenuHovered(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setMailProviderMenuHovered(false);
                }
              }}
            >
              <button
                type="button"
                className="settings-v2-provider-trigger"
                onClick={() => setMailProviderMenuOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={mailProviderMenuOpen}
              >
                <span className="settings-v2-provider-logo" aria-hidden="true">
                  <MailProviderIcon provider={selectedMailProvider.value} />
                </span>
                <span>{selectedMailProvider.label}</span>
                <span className="settings-v2-provider-chevron" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="m5.4 7.8 4.6 4.6 4.6-4.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
              {mailProviderMenuOpen ? (
                <div className="settings-v2-provider-menu" role="listbox">
                  {mailProviderOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`settings-v2-provider-option ${option.value === form.mail.provider ? "is-selected" : ""}`}
                      onClick={() => updateMailProvider(option.value)}
                      role="option"
                      aria-selected={option.value === form.mail.provider}
                    >
                      <span className="settings-v2-provider-logo" aria-hidden="true">
                        <MailProviderIcon provider={option.value} />
                      </span>
                      <span className="settings-v2-provider-option-copy">
                        <strong>{option.label}</strong>
                        <small>{option.description}</small>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {form.mail.provider === "microsoft_graph" ? (
              <div className="settings-v2-mail-provider-extra-grid">
                <div className="form-field settings-v2-mail-provider-extra-field">
                  <label>
                    Freigegebenes Postfach (optional)
                    <span className="settings-v2-label-info" title="Optionales Shared Mailbox Konto">i</span>
                  </label>
                  <input
                    placeholder="z. B. info@firma.de"
                    value={form.mail.microsoftSharedMailboxEmail || ""}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        mail: { ...prev.mail, microsoftSharedMailboxEmail: e.target.value }
                      }))
                    }
                  />
                  <small className="settings-upload-hint">
                    Wenn gesetzt, sendet Microsoft 365 über dieses Postfach (Benutzer braucht Send-As/Send-on-behalf Rechte).
                  </small>
                </div>
                {form.management.faciliooEnabled !== false ? (
                  <div className="form-field settings-v2-mail-provider-extra-field">
                    <label>
                      Rückläufer-Zielordner
                      <span className="settings-v2-label-info" title="Ordner für NDR- und Bounce-Prüfungen">i</span>
                    </label>
                    <select
                      value={selectedMicrosoftNdrFolderValue}
                      onChange={(e) => {
                        const selected = String(e.target.value ?? "").trim();
                        setForm((prev) => ({
                          ...prev,
                          mail: {
                            ...prev.mail,
                            microsoftNdrFolders: selected === NDR_FOLDER_DEFAULT ? "" : selected
                          }
                        }));
                      }}
                    >
                      {microsoftNdrFolderOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="settings-v2-mail-folder-row">
                      <button
                        type="button"
                        className="settings-v2-inline-action"
                        onClick={() =>
                          void loadMicrosoftNdrFolders({
                            mailbox: String(form.mail.microsoftSharedMailboxEmail ?? "").trim()
                          })
                        }
                        disabled={microsoftNdrFoldersLoading || !microsoftMailStatus?.connected}
                      >
                        <span className="settings-v2-inline-action-icon" aria-hidden="true">
                          <svg viewBox="0 0 20 20" fill="none">
                            <path d="M5.1 6.3A5.6 5.6 0 0 1 15 8.8h-2.2M14.9 13.7A5.6 5.6 0 0 1 5 11.2h2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M15 5v3.8h-3.8M5 15v-3.8h3.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        {microsoftNdrFoldersLoading ? "Lade Ordner..." : "Ordner laden"}
                      </button>
                      {microsoftNdrFoldersMailbox ? (
                        <small className="settings-upload-hint">Quelle: {microsoftNdrFoldersMailbox}</small>
                      ) : null}
                    </div>
                    {microsoftNdrFoldersError ? (
                      <small className="settings-upload-hint" style={{ color: "#b42318", fontWeight: 600 }}>
                        {microsoftNdrFoldersError}
                      </small>
                    ) : null}
                    <small className="settings-upload-hint">
                      Dieser Ordner wird zusätzlich zum Posteingang für NDR/Bounce-Prüfungen verwendet.
                    </small>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {form.mail.provider !== "microsoft_graph" ? (
            <>
              <div className="form-field">
                <label>
                  Absendername
                  <span className="settings-v2-label-info" title="Angezeigter Name des Absenders">i</span>
                </label>
                <input value={form.mail.fromName} onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, fromName: e.target.value } }))} />
                <small className="settings-upload-hint">Name, der als Absender angezeigt wird.</small>
              </div>
              <div className="form-field">
                <label>
                  Absender E-Mail
                  <span className="settings-v2-label-info" title="Adresse, von der gesendet wird">i</span>
                </label>
                <input value={form.mail.fromEmail} onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, fromEmail: e.target.value } }))} />
                <small className="settings-upload-hint">Die E-Mail-Adresse, von der aus gesendet wird.</small>
              </div>
            </>
          ) : null}
          {form.mail.provider !== "microsoft_graph" ? (
            <div className="form-field">
              <label>
                Reply-To E-Mail
                <span className="settings-v2-label-info" title="Adresse für Antworten">i</span>
              </label>
              <input value={form.mail.replyToEmail} onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, replyToEmail: e.target.value } }))} />
              <small className="settings-upload-hint">E-Mail-Adresse für Antworten (Reply-To).</small>
            </div>
          ) : null}

          {form.mail.provider === "smtp" ? (
            <>
              <div className="form-field">
                <label>
                  SMTP Host
                  <span className="settings-v2-label-info" title="Hostname des SMTP-Servers">i</span>
                </label>
                <input value={form.mail.smtpHost} onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, smtpHost: e.target.value } }))} />
                <small className="settings-upload-hint">Hostname des SMTP-Servers.</small>
              </div>
              <div className="form-field">
                <label>
                  SMTP Port
                  <span className="settings-v2-label-info" title="Port des SMTP-Servers">i</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="65535"
                  value={form.mail.smtpPort > 0 ? form.mail.smtpPort : ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      mail: {
                        ...prev.mail,
                        smtpPort: Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0
                      }
                    }))
                  }
                />
                <small className="settings-upload-hint">Port des SMTP-Servers.</small>
              </div>
              <div className="form-field">
                <label>
                  SMTP Benutzer
                  <span className="settings-v2-label-info" title="Benutzername für SMTP">i</span>
                </label>
                <input value={form.mail.smtpUser} onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, smtpUser: e.target.value } }))} />
                <small className="settings-upload-hint">Benutzername für die SMTP-Authentifizierung.</small>
              </div>
              <div className="form-field">
                <label>
                  SMTP Passwort
                  <span className="settings-v2-label-info" title="Passwort des SMTP-Benutzers">i</span>
                </label>
                <div className="settings-secret-row">
                  <input
                    type={showSmtpPassword ? "text" : "password"}
                    autoComplete="off"
                    value={form.mail.smtpPassword}
                    onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, smtpPassword: e.target.value } }))}
                  />
                  <button
                    type="button"
                    className="owner-auth-eye-btn"
                    onClick={() => setShowSmtpPassword((v) => !v)}
                    aria-label={showSmtpPassword ? "SMTP Passwort verbergen" : "SMTP Passwort anzeigen"}
                    title={showSmtpPassword ? "SMTP Passwort verbergen" : "SMTP Passwort anzeigen"}
                  >
                    {showSmtpPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                      </svg>
                    )}
                  </button>
                </div>
                <small className="settings-upload-hint">Passwort des SMTP-Benutzers.</small>
              </div>
              <div className="form-field">
                <label>
                  SMTP Secure (TLS)
                  <span className="settings-v2-label-info" title="Verschlüsselung der SMTP-Verbindung">i</span>
                </label>
                <select
                  value={form.mail.smtpSecure ? "true" : "false"}
                  onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, smtpSecure: e.target.value === "true" } }))}
                >
                  <option value="false">Nein</option>
                  <option value="true">Ja</option>
                </select>
                <small className="settings-upload-hint">Verschlüsselungsmethode für die Verbindung.</small>
              </div>
            </>
          ) : null}

          {form.mail.provider === "brevo_api" ? (
            <div className="form-field">
              <label>Brevo API-Key</label>
              <div className="settings-secret-row">
                <input
                  type={showBrevoApiKey ? "text" : "password"}
                  autoComplete="off"
                  value={form.mail.brevoApiKey}
                  onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, brevoApiKey: e.target.value } }))}
                />
                <button
                  type="button"
                  className="owner-auth-eye-btn"
                  onClick={() => setShowBrevoApiKey((v) => !v)}
                  aria-label={showBrevoApiKey ? "Brevo API-Key verbergen" : "Brevo API-Key anzeigen"}
                  title={showBrevoApiKey ? "Brevo API-Key verbergen" : "Brevo API-Key anzeigen"}
                >
                  {showBrevoApiKey ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                      <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M2 12C3.8 7.8 7.5 5 12 5c4.5 0 8.2 2.8 10 7-1.8 4.2-5.5 7-10 7-4.5 0-8.2-2.8-10-7Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                    </svg>
                  )}
                </button>
              </div>
              <small className="settings-upload-hint" style={{ display: "block", marginTop: 8 }}>
                Ihren API-Key finden Sie in Ihrem Brevo-Konto unter Einstellungen &gt; API &amp; Authentifizierung.
              </small>
            </div>
          ) : null}

          {form.mail.provider === "brevo_api" ? (
            <div className="form-field">
              <label>
                Test-Empfänger
                <span className="settings-v2-label-info" title="Adresse für den Testversand">i</span>
              </label>
              <input
                value={form.mail.testRecipientEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, testRecipientEmail: e.target.value } }))}
              />
              <small className="settings-upload-hint" style={{ display: "block", marginTop: 8 }}>
                An diese Adresse wird der Testversand gesendet.
              </small>
            </div>
          ) : null}

          {form.mail.provider === "microsoft_graph" ? (
            <div className="form-field form-field-wide settings-v2-mail-panel settings-v2-mail-connect-panel">
              <div className="settings-v2-mail-connect-content">
                <div className="settings-v2-mail-service">
                  <span className="settings-v2-ms-logo" aria-hidden="true">
                    <MailProviderIcon provider="microsoft_graph" />
                  </span>
                  <div className="settings-v2-mail-service-copy">
                    <h4>Microsoft 365</h4>
                    <div className="settings-v2-mail-status-row">
                      <span className={`settings-v2-mail-status ${microsoftMailStatus?.connected ? "is-connected" : "is-disconnected"}`}>
                        <span aria-hidden="true">{microsoftMailStatus?.connected ? "✓" : "!"}</span>
                        {microsoftMailStatus?.connected ? "verbunden" : "nicht verbunden"}
                      </span>
                      <small>
                        {microsoftMailStatus?.connected
                          ? `Verbunden als ${microsoftMailStatus.accountEmail || "-"}`
                          : "Keine aktive Microsoft 365 Verbindung."}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="settings-v2-mail-actions">
                  <button type="button" className="settings-v2-inline-action" onClick={connectMicrosoftMail} disabled={microsoftMailPending}>
                    <span className="settings-v2-inline-action-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M4.5 10h6.9M8.6 6.1 12.5 10l-3.9 3.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12.8 5.2h2.7A1.5 1.5 0 0 1 17 6.7v6.6a1.5 1.5 0 0 1-1.5 1.5h-2.7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {microsoftMailPending ? "Weiterleitung..." : "Mit Microsoft verbinden"}
                  </button>
                  <button
                    type="button"
                    className="settings-v2-inline-action settings-v2-inline-action--danger"
                    onClick={disconnectMicrosoftMail}
                    disabled={microsoftMailPending || !microsoftMailStatus?.connected}
                  >
                    <span className="settings-v2-inline-action-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="6.1" stroke="currentColor" strokeWidth="1.7" />
                        <path d="m7.8 7.8 4.4 4.4m0-4.4-4.4 4.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>
                    </span>
                    Verbindung trennen
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {form.mail.provider === "brevo_api" ? (
            <div className="form-field form-field-wide settings-v2-mail-brevo-test-action">
              <button type="button" className="settings-v2-inline-action" onClick={runMailTest} disabled={mailTestPending}>
                <span className="settings-v2-inline-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M16.2 4.2 8.8 11.6M16.2 4.2l-4.4 12-3-4.6-4.8-2.4 12.2-5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {mailTestPending ? "Testversand läuft..." : "Testversand senden"}
              </button>
            </div>
          ) : null}

          {form.mail.provider !== "brevo_api" ? (
          <div className="form-field form-field-wide settings-v2-mail-panel settings-v2-mail-test-panel">
            <div className="settings-v2-mail-service settings-v2-mail-service--compact">
              <span className="settings-v2-card-icon settings-v2-card-icon--compact" aria-hidden="true">
                <MailEnvelopeIcon />
              </span>
              <div className="settings-v2-mail-service-copy">
                <h4>Test-Empfänger</h4>
                <small>Senden Sie eine Test-E-Mail, um die Konfiguration zu prüfen.</small>
              </div>
            </div>
            <div className="settings-v2-mail-test-row">
              <input
                value={form.mail.testRecipientEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, mail: { ...prev.mail, testRecipientEmail: e.target.value } }))}
              />
              <button type="button" className="settings-v2-inline-action" onClick={runMailTest} disabled={mailTestPending}>
                <span className="settings-v2-inline-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <path d="M16.2 4.2 8.8 11.6M16.2 4.2l-4.4 12-3-4.6-4.8-2.4 12.2-5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {mailTestPending ? "Testversand läuft..." : "Testversand senden"}
              </button>
            </div>
          </div>
          ) : null}
          {mailTestStatus ? (
            <div className="form-field form-field-wide">
              <small style={{ color: mailTestStatus.type === "ok" ? "#0f7b31" : "#b42318", fontWeight: 600 }}>{mailTestStatus.text}</small>
            </div>
          ) : null}
          {form.mail.provider === "smtp" ? (
            <div className="form-field form-field-wide">
              <div className="imports-sync-info-banner v2-info-banner settings-v2-mail-info">
                <span className="imports-sync-info-icon" aria-hidden="true">i</span>
                <div className="imports-sync-info-copy">
                  <strong>Hinweis</strong>
                  <span>Bitte stellen Sie sicher, dass der SMTP-Server ausgehende Verbindungen zulässt und die Zugangsdaten korrekt sind.</span>
                </div>
              </div>
            </div>
          ) : null}
          {form.mail.provider === "brevo_api" ? (
            <div className="form-field form-field-wide">
              <div className="imports-sync-info-banner v2-info-banner settings-v2-mail-info settings-v2-mail-brevo-info">
                <span className="imports-sync-info-icon" aria-hidden="true">i</span>
                <div className="imports-sync-info-copy">
                  <strong>Hinweise zur Einrichtung</strong>
                  <span>Sie benötigen ein aktives Brevo-Konto und einen gültigen API-Key. Die Absender-E-Mail muss in Brevo als bestätigte Absenderadresse hinterlegt sein.</span>
                  <span>Die E-Mails werden über die Brevo API (v3) versendet.</span>
                </div>
                <a className="settings-v2-inline-action" href="https://developers.brevo.com/" target="_blank" rel="noreferrer">
                  Brevo-Dokumentation
                  <span className="settings-v2-inline-action-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" fill="none">
                      <path d="M8 5H5.8A1.8 1.8 0 0 0 4 6.8v7.4A1.8 1.8 0 0 0 5.8 16h7.4a1.8 1.8 0 0 0 1.8-1.8V12M11 4h5v5M9.5 10.5 16 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </section> : null}

      {tab === "allgemein" && !isPlatformSettingsHost ? <section className="panel create-form settings-section settings-v2-section settings-v2-general-card settings-v2-reminders-card">
        <div className="settings-v2-card-head">
          <span className="settings-v2-card-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 5.2a4.4 4.4 0 0 0-4.4 4.4v2.6c0 1-.3 1.9-.9 2.7l-1 1.4h12.6l-1-1.4c-.6-.8-.9-1.7-.9-2.7V9.6A4.4 4.4 0 0 0 12 5.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.4 18.1a1.8 1.8 0 0 0 3.2 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <div className="settings-v2-card-title">
            <h3>{"Erinnerungen Umlaufbeschl\u00FCsse"}</h3>
            <p>Legen Sie fest, wie viele Tage vor dem Enddatum automatisch Erinnerungen an Eigentümer ohne Stimmabgabe versendet werden sollen.</p>
          </div>
        </div>
        <div className="form-grid settings-v2-reminder-grid">
          <div className="form-field">
            <label>1. Erinnerung (Tage vor Enddatum)</label>
            <input
              type="number"
              min="1"
              max="365"
              step="1"
              value={form.reminders.firstReminderDays}
              onChange={(e) => onReminderDaysChange("firstReminderDays", e.target.valueAsNumber)}
            />
          </div>
          <div className="form-field">
            <label>2. Erinnerung (Tage vor Enddatum)</label>
            <input
              type="number"
              min="1"
              max="365"
              step="1"
              value={form.reminders.secondReminderDays}
              onChange={(e) => onReminderDaysChange("secondReminderDays", e.target.valueAsNumber)}
            />
          </div>
          <div className="form-field form-field-wide">
            <div className="imports-sync-info-banner v2-info-banner settings-v2-reminder-info">
              <span className="imports-sync-info-icon" aria-hidden="true">i</span>
              <div className="imports-sync-info-copy">
                <span>{"Automatischer Versand nur an Eigent\u00FCmer ohne Stimmabgabe."}</span>
                <span>Empfehlung: 1. Erinnerung 7 Tage, 2. Erinnerung 3 Tage vor Enddatum.</span>
              </div>
            </div>
          </div>
        </div>
      </section> : null}

      {tab === "design" ? (
        <div className="settings-v2-design-page">
          <section className="panel create-form settings-section settings-v2-section settings-v2-design-card">
            <div className="settings-v2-card-head">
              <span className="settings-v2-card-icon settings-v2-design-icon settings-v2-design-icon--blue" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="m15.7 4.7 3.6 3.6L9.6 18H6v-3.6l9.7-9.7Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="m13.9 6.5 3.6 3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <div className="settings-v2-card-title">
                <h3>Designfarben Backend</h3>
                <p>Definieren Sie die Farben für das Backend-Interface.</p>
              </div>
            </div>
            <div className="form-grid settings-v2-design-grid">
              {renderDesignColorField("Inhaltsseite – Hintergrund", form.theme.bg, (value) => setForm({ ...form, theme: { ...form.theme, bg: value } }), "Hintergrundfarbe der Inhaltsseiten")}
              {renderDesignColorField("Fläche", form.theme.surface, (value) => setForm({ ...form, theme: { ...form.theme, surface: value } }), "Hintergrundfarbe für Flächen und Karten")}
              {renderDesignColorField("Header – Hintergrund", form.theme.headerBg, (value) => setForm({ ...form, theme: { ...form.theme, headerBg: value } }), "Hintergrundfarbe des Headers")}
              {renderDesignColorField("Linien", form.theme.line, (value) => setForm({ ...form, theme: { ...form.theme, line: value } }), "Farbe für Trennlinien und Umrandungen")}
              {renderDesignColorField("Text", form.theme.text, (value) => setForm({ ...form, theme: { ...form.theme, text: value } }), "Primäre Textfarbe")}
              {renderDesignColorField("Akzent", form.theme.primary, (value) => setForm({ ...form, theme: { ...form.theme, primary: value } }), "Akzentfarbe für Links und aktive Elemente")}
              {renderDesignColorField("Listen Spaltenkopf – Hintergrund", form.theme.listHeaderBg, (value) => setForm({ ...form, theme: { ...form.theme, listHeaderBg: value } }), "Hintergrundfarbe für Tabellen-/Listen Spaltenköpfe")}
              {renderDesignColorField("Listen alternierende Zeile", form.theme.listAlternateRowBg, (value) => setForm({ ...form, theme: { ...form.theme, listAlternateRowBg: value } }), "Hintergrundfarbe für alternierende Tabellenzeilen")}
            </div>
          </section>

          <section className="panel create-form settings-section settings-v2-section settings-v2-design-card">
            <div className="settings-v2-card-head">
              <span className="settings-v2-card-icon settings-v2-design-icon settings-v2-design-icon--cyan" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="5" width="4.8" height="14" rx="1.2" stroke="currentColor" strokeWidth="1.8" />
                  <rect x="13.2" y="5" width="4.8" height="14" rx="1.2" stroke="currentColor" strokeWidth="1.8" />
                </svg>
              </span>
              <div className="settings-v2-card-title">
                <h3>Seitenverlauf Backend</h3>
                <p>Konfigurieren Sie den Farbverlauf für den Seitenhintergrund.</p>
              </div>
            </div>
            <div className="form-grid settings-v2-design-grid">
              {renderDesignToggleField("Seitenverlauf aktiv", form.theme.gradientEnabled, (checked) => setForm({ ...form, theme: { ...form.theme, gradientEnabled: checked } }))}
              {renderDesignColorField("Seitenverlauf von", form.theme.gradientFrom, (value) => setForm({ ...form, theme: { ...form.theme, gradientFrom: value } }), "Startfarbe des Verlaufs", !form.theme.gradientEnabled)}
              {renderDesignColorField("Seitenverlauf bis", form.theme.gradientTo, (value) => setForm({ ...form, theme: { ...form.theme, gradientTo: value } }), "Endfarbe des Verlaufs", !form.theme.gradientEnabled)}
            </div>
          </section>

          <section className="panel create-form settings-section settings-v2-section settings-v2-design-card">
            <div className="settings-v2-card-head">
              <span className="settings-v2-card-icon settings-v2-design-icon settings-v2-design-icon--green" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M6 7h12M6 12h12M6 17h12" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </svg>
              </span>
              <div className="settings-v2-card-title">
                <h3>Menü</h3>
                <p>Passen Sie Farben und Zustände des Menüs an.</p>
              </div>
            </div>
            <div className="form-grid settings-v2-design-grid">
              {renderDesignColorField("Menü – Hintergrund", form.theme.navBg, (value) => setForm({ ...form, theme: { ...form.theme, navBg: value } }), "Hintergrund des Menüs")}
              {renderDesignColorField("Menü – Text", form.theme.navText, (value) => setForm({ ...form, theme: { ...form.theme, navText: value } }), "Textfarbe im Menü")}
              {renderDesignColorField("Menü – Aktiv/Hover Hintergrund", form.theme.navActive, (value) => setForm({ ...form, theme: { ...form.theme, navActive: value } }), "Hintergrundfarbe bei Aktiv oder Hover")}
              {renderDesignColorField("Menü – Aktiv/Hover Text", form.theme.navActiveText, (value) => setForm({ ...form, theme: { ...form.theme, navActiveText: value } }), "Textfarbe bei Aktiv oder Hover")}
              {renderDesignColorField("Menü – Rand", form.theme.navBorder, (value) => setForm({ ...form, theme: { ...form.theme, navBorder: value } }), "Randfarbe des Menüs")}
            </div>
          </section>

          <section className="panel create-form settings-section settings-v2-section settings-v2-design-card">
            <div className="settings-v2-card-head">
              <span className="settings-v2-card-icon settings-v2-design-icon settings-v2-design-icon--purple" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 12.2a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Z" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M5.2 19.2a6.8 6.8 0 0 1 13.6 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <div className="settings-v2-card-title">
                <h3>Design Owner-Bereich (Login &amp; Abstimmung)</h3>
                <p>Definieren Sie die Farben für den Login- und Abstimmungsbereich.</p>
              </div>
            </div>
            <div className="form-grid settings-v2-design-grid">
              {renderDesignColorField("Hintergrund", form.ownerTheme.bg, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, bg: value } }), "Hintergrundfarbe des Bereichs")}
              {renderDesignColorField("Fläche", form.ownerTheme.surface, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, surface: value } }), "Flächenfarbe (z. B. Karten, Boxen)")}
              {renderDesignColorField("Linien", form.ownerTheme.line, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, line: value } }), "Linienfarbe im Bereich")}
              {renderDesignColorField("Text", form.ownerTheme.text, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, text: value } }), "Primäre Textfarbe im Bereich")}
              {renderDesignColorField("Akzent", form.ownerTheme.primary, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, primary: value } }), "Akzentfarbe im Bereich")}
            </div>
          </section>

          <section className="panel create-form settings-section settings-v2-section settings-v2-design-card">
            <div className="settings-v2-card-head">
              <span className="settings-v2-card-icon settings-v2-design-icon settings-v2-design-icon--cyan" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M5.2 17.2c3.4-4 6.8-6 10.2-6h3.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M5.2 6.8h13.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M5.2 12h5.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <div className="settings-v2-card-title">
                <h3>Seitenverlauf Owner-Bereich</h3>
                <p>Konfigurieren Sie den Farbverlauf für Login- und Abstimmungsseiten.</p>
              </div>
            </div>
            <div className="form-grid settings-v2-design-grid">
              {renderDesignToggleField("Seitenverlauf aktiv", form.ownerTheme.gradientEnabled, (checked) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, gradientEnabled: checked } }))}
              {renderDesignColorField("Seitenverlauf von", form.ownerTheme.gradientFrom, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, gradientFrom: value } }), "Startfarbe des Verlaufs", !form.ownerTheme.gradientEnabled)}
              {renderDesignColorField("Seitenverlauf bis", form.ownerTheme.gradientTo, (value) => setForm({ ...form, ownerTheme: { ...form.ownerTheme, gradientTo: value } }), "Endfarbe des Verlaufs", !form.ownerTheme.gradientEnabled)}
            </div>
          </section>
        </div>
      ) : null}

      {tab === "uploads" && !isPlatformSettingsHost ? <section className="panel create-form settings-section settings-v2-section settings-v2-upload-page">
        <div className="settings-v2-card-head settings-v2-upload-head">
          <span className="settings-v2-card-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5.2 17.6h13.6V9.4H5.2v8.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="m7.2 15.8 3.1-3.1 2.1 2.1 2.7-2.7 2.7 3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.2 8.1 12 4.4l3.8 3.7M12 4.8v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <div className="settings-v2-card-title">
            <h3>Dateiuploads</h3>
            <p>Verwalten Sie Logos und Briefpapiere für Ihre Kommunikation.</p>
          </div>
        </div>
        <div className="settings-upload-shell">
          <div className="settings-upload-grid">
            <div
              className={`settings-upload-card${uploadDropTarget === "logo-backend" ? " is-drag-over" : ""}`}
              onDragOver={(e) => onUploadDragOver(e, "logo-backend")}
              onDragLeave={onUploadDragLeave}
              onDrop={(e) => onUploadDrop(e, "logo-backend")}
            >
              <h4><UploadCardIcon variant="image" /><span>Logo Backend</span></h4>
              <small className="settings-upload-hint">
                Empfehlung: 320 x 72 px (Anzeige 160 x 36 px), SVG oder PNG mit transparentem Hintergrund.
              </small>
              <small className="settings-upload-hint">Datei per Drag-and-Drop hier ablegen oder über den Button auswählen.</small>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => onLogoFile(e.target.files?.[0] ?? null)}
              />
              <div className="settings-logo-actions">
                <button type="button" onClick={() => logoInputRef.current?.click()}>
                  <UploadActionIcon kind="upload" />
                  Datei auswählen
                </button>
                <button type="button" onClick={onLogoRemove} disabled={!form.logoDataUrl}>
                  <UploadActionIcon kind="delete" />
                  Logo löschen
                </button>
              </div>
              {form.logoDataUrl ? <img src={form.logoDataUrl} alt="Logo Vorschau" className="settings-logo-preview" /> : null}
            </div>

            <div
              className={`settings-upload-card${uploadDropTarget === "logo-owner" ? " is-drag-over" : ""}`}
              onDragOver={(e) => onUploadDragOver(e, "logo-owner")}
              onDragLeave={onUploadDragLeave}
              onDrop={(e) => onUploadDrop(e, "logo-owner")}
            >
              <h4><UploadCardIcon variant="user" /><span>Logo Owner-Bereich</span></h4>
              <small className="settings-upload-hint">
                Empfehlung: 666 x 200 px (Desktop max. 333 x 100 px), SVG oder PNG mit transparentem Hintergrund.
              </small>
              <small className="settings-upload-hint">Datei per Drag-and-Drop hier ablegen oder über den Button auswählen.</small>
              <input
                ref={ownerLogoInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => onOwnerLogoFile(e.target.files?.[0] ?? null)}
              />
              <div className="settings-logo-actions">
                <button type="button" onClick={() => ownerLogoInputRef.current?.click()}>
                  <UploadActionIcon kind="upload" />
                  Datei auswählen
                </button>
                <button type="button" onClick={onOwnerLogoRemove} disabled={!form.ownerLogoDataUrl}>
                  <UploadActionIcon kind="delete" />
                  Logo löschen
                </button>
              </div>
              {form.ownerLogoDataUrl ? <img src={form.ownerLogoDataUrl} alt="Owner-Logo Vorschau" className="settings-logo-preview" /> : null}
            </div>

            <div
              className={`settings-upload-card${uploadDropTarget === "letterhead-digital" ? " is-drag-over" : ""}`}
              onDragOver={(e) => onUploadDragOver(e, "letterhead-digital")}
              onDragLeave={onUploadDragLeave}
              onDrop={(e) => onUploadDrop(e, "letterhead-digital")}
            >
              <h4><UploadCardIcon variant="document" /><span>Briefpapier digital</span></h4>
              <small className="settings-upload-hint">PDF/PNG/JPG für digitale Schreiben.</small>
              <small className="settings-upload-hint">Datei per Drag-and-Drop hier ablegen oder über den Button auswählen.</small>
              <input
                ref={letterheadDigitalInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(e) => void onLetterheadUpload("digital", e.target.files?.[0] ?? null)}
              />
              <div className="settings-logo-actions">
                <button type="button" onClick={() => letterheadDigitalInputRef.current?.click()} disabled={letterheadPending === "digital"}>
                  <UploadActionIcon kind="upload" />
                  {letterheadPending === "digital" ? "Upload..." : "Datei auswählen"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!letterheads.digital?.fileUrl) return;
                    window.open(letterheads.digital.fileUrl, "_blank", "noopener,noreferrer");
                  }}
                  disabled={!letterheads.digital?.fileUrl}
                >
                  <UploadActionIcon kind="open" />
                  Öffnen
                </button>
                <button
                  type="button"
                  onClick={() => void onLetterheadDelete("digital")}
                  disabled={!letterheads.digital?.fileUrl || letterheadDeletePending === "digital"}
                >
                  <UploadActionIcon kind="delete" />
                  {letterheadDeletePending === "digital" ? "Löschen..." : "Datei löschen"}
                </button>
              </div>
              <div className="settings-grid-2col" style={{ marginTop: 8 }}>
                <div>
                  <label>Oberer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("digital").topMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("digital", "topMarginMm", e.target.value)}
                  />
                </div>
                <div>
                  <label>Unterer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("digital").bottomMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("digital", "bottomMarginMm", e.target.value)}
                  />
                </div>
              </div>
              <div className="settings-logo-actions" style={{ marginTop: 8 }}>
                <button type="button" onClick={() => void onLetterheadMarginsSave("digital")} disabled={letterheadMarginsPending === "digital"}>
                  <UploadActionIcon kind="save" />
                  {letterheadMarginsPending === "digital" ? "Speichern..." : "Ränder speichern"}
                </button>
              </div>
              <div className="settings-grid-2col" style={{ marginTop: 8 }}>
                <div>
                  <label>Adressat von oben (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={260}
                    step={0.1}
                    value={getLetterheadFirstPageLayout("digital").firstPageRecipientTopMm}
                    onChange={(e) => onLetterheadFirstPageLayoutChange("digital", "firstPageRecipientTopMm", e.target.value)}
                  />
                </div>
                <div>
                  <label>Textstart von oben (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={260}
                    step={0.1}
                    value={getLetterheadFirstPageLayout("digital").firstPageBodyStartMm}
                    onChange={(e) => onLetterheadFirstPageLayoutChange("digital", "firstPageBodyStartMm", e.target.value)}
                  />
                </div>
              </div>
              <div className="settings-logo-actions" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => void onLetterheadFirstPageLayoutSave("digital")}
                  disabled={letterheadFirstPageLayoutPending === "digital"}
                >
                  <UploadActionIcon kind="save" />
                  {letterheadFirstPageLayoutPending === "digital" ? "Speichern..." : "Erstseite speichern"}
                </button>
              </div>
              <small className={`settings-upload-hint settings-upload-state ${letterheads.digital?.fileName ? "is-ok" : "is-warn"}`}>
                <UploadStatusIcon kind={letterheads.digital?.fileName ? "ok" : "warn"} />
                {letterheads.digital?.fileName
                  ? `Briefpapier hinterlegt | Stand: ${formatDateTimeDE(letterheads.digital.updatedAt)}`
                  : "Kein Briefpapier hinterlegt"}
              </small>
              {letterheads.digital?.fileName ? <small className="settings-upload-hint">Datei: {letterheads.digital.fileName}</small> : null}
            </div>

            <div
              className={`settings-upload-card${uploadDropTarget === "letterhead-analog" ? " is-drag-over" : ""}`}
              onDragOver={(e) => onUploadDragOver(e, "letterhead-analog")}
              onDragLeave={onUploadDragLeave}
              onDrop={(e) => onUploadDrop(e, "letterhead-analog")}
            >
              <h4><UploadCardIcon variant="document" /><span>Briefpapier analog</span></h4>
              <small className="settings-upload-hint">PDF/PNG/JPG für analoge Schreiben.</small>
              <small className="settings-upload-hint">Datei per Drag-and-Drop hier ablegen oder über den Button auswählen.</small>
              <input
                ref={letterheadAnalogInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(e) => void onLetterheadUpload("analog", e.target.files?.[0] ?? null)}
              />
              <div className="settings-logo-actions">
                <button type="button" onClick={() => letterheadAnalogInputRef.current?.click()} disabled={letterheadPending === "analog"}>
                  <UploadActionIcon kind="upload" />
                  {letterheadPending === "analog" ? "Upload..." : "Datei auswählen"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!letterheads.analog?.fileUrl) return;
                    window.open(letterheads.analog.fileUrl, "_blank", "noopener,noreferrer");
                  }}
                  disabled={!letterheads.analog?.fileUrl}
                >
                  <UploadActionIcon kind="open" />
                  Öffnen
                </button>
                <button
                  type="button"
                  onClick={() => void onLetterheadDelete("analog")}
                  disabled={!letterheads.analog?.fileUrl || letterheadDeletePending === "analog"}
                >
                  <UploadActionIcon kind="delete" />
                  {letterheadDeletePending === "analog" ? "Löschen..." : "Datei löschen"}
                </button>
              </div>
              <div className="settings-grid-2col" style={{ marginTop: 8 }}>
                <div>
                  <label>Oberer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("analog").topMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("analog", "topMarginMm", e.target.value)}
                  />
                </div>
                <div>
                  <label>Unterer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("analog").bottomMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("analog", "bottomMarginMm", e.target.value)}
                  />
                </div>
              </div>
              <div className="settings-logo-actions" style={{ marginTop: 8 }}>
                <button type="button" onClick={() => void onLetterheadMarginsSave("analog")} disabled={letterheadMarginsPending === "analog"}>
                  <UploadActionIcon kind="save" />
                  {letterheadMarginsPending === "analog" ? "Speichern..." : "Ränder speichern"}
                </button>
              </div>
              <div className="settings-grid-2col" style={{ marginTop: 8 }}>
                <div>
                  <label>Adressat von oben (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={260}
                    step={0.1}
                    value={getLetterheadFirstPageLayout("analog").firstPageRecipientTopMm}
                    onChange={(e) => onLetterheadFirstPageLayoutChange("analog", "firstPageRecipientTopMm", e.target.value)}
                  />
                </div>
                <div>
                  <label>Textstart von oben (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={260}
                    step={0.1}
                    value={getLetterheadFirstPageLayout("analog").firstPageBodyStartMm}
                    onChange={(e) => onLetterheadFirstPageLayoutChange("analog", "firstPageBodyStartMm", e.target.value)}
                  />
                </div>
              </div>
              <div className="settings-logo-actions" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => void onLetterheadFirstPageLayoutSave("analog")}
                  disabled={letterheadFirstPageLayoutPending === "analog"}
                >
                  <UploadActionIcon kind="save" />
                  {letterheadFirstPageLayoutPending === "analog" ? "Speichern..." : "Erstseite speichern"}
                </button>
              </div>
              <small className={`settings-upload-hint settings-upload-state ${letterheads.analog?.fileName ? "is-ok" : "is-warn"}`}>
                <UploadStatusIcon kind={letterheads.analog?.fileName ? "ok" : "warn"} />
                {letterheads.analog?.fileName
                  ? `Briefpapier hinterlegt | Stand: ${formatDateTimeDE(letterheads.analog.updatedAt)}`
                  : "Kein Briefpapier hinterlegt"}
              </small>
              {letterheads.analog?.fileName ? <small className="settings-upload-hint">Datei: {letterheads.analog.fileName}</small> : null}
            </div>

            <div
              className={`settings-upload-card${uploadDropTarget === "letterhead-digital-followup" ? " is-drag-over" : ""}`}
              onDragOver={(e) => onUploadDragOver(e, "letterhead-digital-followup")}
              onDragLeave={onUploadDragLeave}
              onDrop={(e) => onUploadDrop(e, "letterhead-digital-followup")}
            >
              <h4><UploadCardIcon variant="document" /><span>Briefpapier digital (ab Seite 2)</span></h4>
              <small className="settings-upload-hint">PDF/PNG/JPG für Folgeseiten digitaler Schreiben.</small>
              <small className="settings-upload-hint">Datei per Drag-and-Drop hier ablegen oder über den Button auswählen.</small>
              <input
                ref={letterheadDigitalFollowupInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(e) => void onLetterheadUpload("digital_followup", e.target.files?.[0] ?? null)}
              />
              <div className="settings-logo-actions">
                <button type="button" onClick={() => letterheadDigitalFollowupInputRef.current?.click()} disabled={letterheadPending === "digital_followup"}>
                  <UploadActionIcon kind="upload" />
                  {letterheadPending === "digital_followup" ? "Upload..." : "Datei auswählen"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!letterheads.digital_followup?.fileUrl) return;
                    window.open(letterheads.digital_followup.fileUrl, "_blank", "noopener,noreferrer");
                  }}
                  disabled={!letterheads.digital_followup?.fileUrl}
                >
                  <UploadActionIcon kind="open" />
                  Öffnen
                </button>
                <button
                  type="button"
                  onClick={() => void onLetterheadDelete("digital_followup")}
                  disabled={!letterheads.digital_followup?.fileUrl || letterheadDeletePending === "digital_followup"}
                >
                  <UploadActionIcon kind="delete" />
                  {letterheadDeletePending === "digital_followup" ? "Löschen..." : "Datei löschen"}
                </button>
              </div>
              <div className="settings-grid-2col" style={{ marginTop: 8 }}>
                <div>
                  <label>Oberer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("digital_followup").topMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("digital_followup", "topMarginMm", e.target.value)}
                  />
                </div>
                <div>
                  <label>Unterer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("digital_followup").bottomMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("digital_followup", "bottomMarginMm", e.target.value)}
                  />
                </div>
              </div>
              <div className="settings-logo-actions" style={{ marginTop: 8 }}>
                <button type="button" onClick={() => void onLetterheadMarginsSave("digital_followup")} disabled={letterheadMarginsPending === "digital_followup"}>
                  <UploadActionIcon kind="save" />
                  {letterheadMarginsPending === "digital_followup" ? "Speichern..." : "Ränder speichern"}
                </button>
              </div>
              <small className={`settings-upload-hint settings-upload-state ${letterheads.digital_followup?.fileName ? "is-ok" : "is-warn"}`}>
                <UploadStatusIcon kind={letterheads.digital_followup?.fileName ? "ok" : "warn"} />
                {letterheads.digital_followup?.fileName
                  ? `Briefpapier hinterlegt | Stand: ${formatDateTimeDE(letterheads.digital_followup.updatedAt)}`
                  : "Kein Briefpapier hinterlegt"}
              </small>
              {letterheads.digital_followup?.fileName ? <small className="settings-upload-hint">Datei: {letterheads.digital_followup.fileName}</small> : null}
            </div>

            <div
              className={`settings-upload-card${uploadDropTarget === "letterhead-analog-followup" ? " is-drag-over" : ""}`}
              onDragOver={(e) => onUploadDragOver(e, "letterhead-analog-followup")}
              onDragLeave={onUploadDragLeave}
              onDrop={(e) => onUploadDrop(e, "letterhead-analog-followup")}
            >
              <h4><UploadCardIcon variant="document" /><span>Briefpapier analog (ab Seite 2)</span></h4>
              <small className="settings-upload-hint">PDF/PNG/JPG für Folgeseiten analoger Schreiben.</small>
              <small className="settings-upload-hint">Datei per Drag-and-Drop hier ablegen oder über den Button auswählen.</small>
              <input
                ref={letterheadAnalogFollowupInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(e) => void onLetterheadUpload("analog_followup", e.target.files?.[0] ?? null)}
              />
              <div className="settings-logo-actions">
                <button type="button" onClick={() => letterheadAnalogFollowupInputRef.current?.click()} disabled={letterheadPending === "analog_followup"}>
                  <UploadActionIcon kind="upload" />
                  {letterheadPending === "analog_followup" ? "Upload..." : "Datei auswählen"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!letterheads.analog_followup?.fileUrl) return;
                    window.open(letterheads.analog_followup.fileUrl, "_blank", "noopener,noreferrer");
                  }}
                  disabled={!letterheads.analog_followup?.fileUrl}
                >
                  <UploadActionIcon kind="open" />
                  Öffnen
                </button>
                <button
                  type="button"
                  onClick={() => void onLetterheadDelete("analog_followup")}
                  disabled={!letterheads.analog_followup?.fileUrl || letterheadDeletePending === "analog_followup"}
                >
                  <UploadActionIcon kind="delete" />
                  {letterheadDeletePending === "analog_followup" ? "Löschen..." : "Datei löschen"}
                </button>
              </div>
              <div className="settings-grid-2col" style={{ marginTop: 8 }}>
                <div>
                  <label>Oberer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("analog_followup").topMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("analog_followup", "topMarginMm", e.target.value)}
                  />
                </div>
                <div>
                  <label>Unterer Rand (mm)</label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={0.1}
                    value={getLetterheadMargins("analog_followup").bottomMarginMm}
                    onChange={(e) => onLetterheadMarginsChange("analog_followup", "bottomMarginMm", e.target.value)}
                  />
                </div>
              </div>
              <div className="settings-logo-actions" style={{ marginTop: 8 }}>
                <button type="button" onClick={() => void onLetterheadMarginsSave("analog_followup")} disabled={letterheadMarginsPending === "analog_followup"}>
                  <UploadActionIcon kind="save" />
                  {letterheadMarginsPending === "analog_followup" ? "Speichern..." : "Ränder speichern"}
                </button>
              </div>
              <small className={`settings-upload-hint settings-upload-state ${letterheads.analog_followup?.fileName ? "is-ok" : "is-warn"}`}>
                <UploadStatusIcon kind={letterheads.analog_followup?.fileName ? "ok" : "warn"} />
                {letterheads.analog_followup?.fileName
                  ? `Briefpapier hinterlegt | Stand: ${formatDateTimeDE(letterheads.analog_followup.updatedAt)}`
                  : "Kein Briefpapier hinterlegt"}
              </small>
              {letterheads.analog_followup?.fileName ? <small className="settings-upload-hint">Datei: {letterheads.analog_followup.fileName}</small> : null}
            </div>
          </div>
          <div className="imports-sync-info-banner v2-info-banner settings-v2-upload-info">
            <span className="imports-sync-info-icon" aria-hidden="true">i</span>
            <div className="imports-sync-info-copy">
              <strong>Hinweis</strong>
              <span>Die hinterlegten Briefpapiere werden für die Erstellung von Dokumenten und Schreiben verwendet. Achten Sie auf korrekte Ränder und Abstände.</span>
            </div>
          </div>
        </div>
      </section> : null}

      {tab === "datenbestand" ? <section className="panel create-form settings-section settings-section-danger settings-v2-section">
        <h3 style={{ marginTop: 0 }}>Datenbestände</h3>
        <div className="settings-danger-grid">
          <div className="settings-danger-card">
            <h4>Datenbestand löschen</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Datentyp</label>
                <select value={dataClearType} onChange={(e) => setDataClearType(e.target.value as DataInventoryType)}>
                  <option value="objects">Objekte (inkl. Einheiten, Eigentümer, Zuordnungen, Beschlüsse)</option>
                  <option value="units">Einheiten (inkl. Zuordnungen)</option>
                  <option value="owners">Eigentümer (inkl. Zuordnungen)</option>
                  <option value="assignments">Zuordnungen</option>
                  <option value="resolutions">Beschlüsse</option>
                  <option value="all">Alles (kompletter Bestand)</option>
                </select>
              </div>
              <div className="form-field">
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="settings-danger-action-btn"
                  onClick={() => {
                    const code = String(Math.floor(1000 + Math.random() * 9000));
                    setDataClearConfirm({ code, input: ["", "", "", ""], error: "" });
                  }}
                  disabled={dataClearPending}
                >
                  <UploadActionIcon kind="delete" />
                  {dataClearPending ? "Löschen läuft..." : "Datenbestand löschen"}
                </button>
              </div>
              {dataClearStatus ? (
                <div className="form-field form-field-wide">
                  <small style={{ color: dataClearStatus.type === "ok" ? "#0f7b31" : "#b42318", fontWeight: 600 }}>
                    {dataClearStatus.text}
                  </small>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section> : null}



      <DeleteCodeConfirmModal
        open={!!dataClearConfirm}
        title="Datenbestand löschen bestätigen"
        message={
          dataClearConfirm
            ? `Der Datenbestand "${dataClearTypeLabel[dataClearType]}" wird gelöscht. Dieser Schritt ist endgültig.`
            : ""
        }
        code={dataClearConfirm?.code ?? ""}
        input={dataClearConfirm?.input ?? ["", "", "", ""]}
        error={dataClearConfirm?.error}
        isPending={dataClearPending}
        onClose={() => setDataClearConfirm(null)}
        onInputChange={(next) => {
          if (!dataClearConfirm) return;
          setDataClearConfirm({ ...dataClearConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!dataClearConfirm || !dataClearConfirm.error) return;
          setDataClearConfirm({ ...dataClearConfirm, error: "" });
        }}
        confirmLabel="Datenbestand löschen"
        pendingLabel="Löschen läuft..."
        onConfirm={() => {
          if (!dataClearConfirm) return;
          const enteredCode = dataClearConfirm.input.join("");
          if (enteredCode !== dataClearConfirm.code) {
            setDataClearConfirm({ ...dataClearConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          setDataClearConfirm(null);
          void runClearDataByType();
        }}
      />
    </div>
  );
};








